'use client';
/* eslint-disable @next/next/no-img-element -- collections UI scaffold: placeholder demo imgs */ // ---------------------------------------------------------------------------
// Collections library — the LEFT-panel "Library" scope.
//
// Wired to the persistent libraryStore: the user builds a library with their
// OWN data (metadata + uploaded blobs survive reloads via IndexedDB). Sample
// data is one click away and gates the demo-flavored flourishes.
//
// Structure is USER-AUTHORED: a folder tree (one or two levels; drag rows onto
// folder headers, right-click to rename/move/delete, drop OS files to import)
// plus tags for the cross-cutting dimensions a single-parent tree can't express
// — reachable through search (# tokens) and the Filter control, never on rows.
//
// The browse surface stays quiet: one-line rows (thumb + name, a lock glyph
// only when it means something), details on hover, judgment in the inspector.
// Selecting a collection opens its edit view on the right.
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { createPortal } from 'react-dom';
import { ArrowUpDown, ChevronRight, Download, Folder, FolderPlus, Braces, ImageIcon, MapPin, Plus, RotateCcw, Rows3, Search, Upload, User, Video, X, Info } from '@/components/ui/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { libraryTagFor } from '@/components/collections/libraryMention';
import { getStorageUrl } from '@/lib/api';
import { getAssetDisplayUri } from '@/lib/assetHelpers';
import { setImageDrag, clearImageDrag } from '@/lib/imageDragEvent';
import { AssetLightbox, TextTile } from '@/components/collections/views';
import { useStore as useCanvasStore } from '@/store/yjsStore';
import { Input } from '@/components/ui/input';
import CollectionInspector from './CollectionInspector';
import { FilterControl, IconSelect, type Filters } from './controls';
import { ImagePreviewHover } from '@/components/generation/video/components/ImagePreviewHover';
import { LIBRARY_VIDEO_DRAG_MIME } from '@/components/generation/video/libraryVideoDrag';
import { getAssetUrl, heroAsset, libraryActions, selectLibraryWriteAccess, useLibraryStore, type LibAsset, type LibCollection } from '@/store/libraryStore';
import { consumeVariableDrag, subscribeToVariableDragActive } from '@/lib/variableDragEvent';
import { consumeSubjectDrag, subscribeToSubjectDragActive } from '@/lib/subjectDragEvent';
import { trackLibraryProjectLinkChanged, trackLibrarySearched, trackLibraryTagCopied } from '@/lib/analytics';
import { canvasAssetDisplayName, importSubjectToLibrary, saveCanvasAssetToLibrary, saveCanvasAssetsToLibrary } from './saveToLibrary';
import { CommunityLibraryDialog } from './CommunityLibraryDialog';
import { LibraryAccessDeniedNotice } from './LibraryAccessDeniedNotice';
import { LibraryLoadFailedNotice } from './LibraryLoadFailedNotice';
import { SAMPLE_LIBRARY_AVAILABLE, SAMPLE_PREVIEW } from './sampleLibrary';
import { formatAttrValue } from './propertyValues';
import { nextAssetSelection } from './assetSelection';
import { emitLibraryRevealRequest } from '@/lib/floatingLeftPanelEvents';
const KEY_GROUP = 'martini-collection-group-v3';
const KEY_SORT = 'martini-collection-sort';
const KEY_COL_WIDTH = 'martini-extension-column-width';
const KEY_INS_WIDTH = 'martini-library-inline-inspector-width';
const KEY_POOL_OPEN = 'martini-library-pool-open';
const GROUPS = [
    'Folders',
    'None'
] as const;
const SORTS = [
    'Updated',
    'Name',
    'Used',
    'Assets'
] as const;
type GroupBy = (typeof GROUPS)[number];
type SortKey = (typeof SORTS)[number];
function libraryMoveIds(dataTransfer: DataTransfer): string[] {
    const many = dataTransfer.getData('application/x-martini-library-move-many');
    if (many) {
        try {
            const parsed = JSON.parse(many) as {
                ids?: unknown;
            };
            if (Array.isArray(parsed.ids)) return parsed.ids.filter((id): id is string =>typeof id === 'string');
        } catch  {
            return [];
        }
    }
    const one = dataTransfer.getData('application/x-martini-library-move');
    return one ? [
        one
    ] : [];
}
// Collection-level filter facets. Tags is the cross-cutting dimension.
type FacetSpec = {
    key: string;
    label: string;
    values: string[];
    get: (c: LibCollection) => string;
};
/** OR within a facet, AND across; Tags matches any of the collection's tags. */ function applyFilters(list: LibCollection[], filters: Filters, getFor: Record<string, (c: LibCollection) => string>): LibCollection[] {
    let out = list;
    for (const [key, vals] of Object.entries(filters)){
        if (!vals.length) continue;
        if (key === 'Tags') out = out.filter((c)=>c.tags.some((t)=>vals.includes(t)));
        else {
            const get = getFor[key];
            if (get) out = out.filter((c)=>vals.includes(get(c)));
        }
    }
    return out;
}
function readPref<T extends string>(key: string, valid: readonly T[], fallback: T): T {
    if (typeof window === 'undefined') return fallback;
    const v = localStorage.getItem(key) as T | null;
    return v && (valid as readonly string[]).includes(v) ? v : fallback;
}
// --- Row (one line — details live on hover and in the inspector) ------------
function Row({ c, depth, selected, renaming, thumb, count, onOpen, onContext, onStartRename, onCommitRename, onCancelRename, peek, onMoveDragOver, onMoveDrop, moveOver, readOnly = false }: {
    c: LibCollection;
    depth: number;
    selected: boolean;
    renaming: boolean;
    thumb: string;
    count: number;
    onOpen: () => void;
    onContext: (e: React.MouseEvent) => void;
    /** Double-click anywhere on the row starts an inline rename. */ onStartRename: () => void;
    onCommitRename: (name: string) => void;
    onCancelRename: () => void;
    /** Hover-peek: float this row's assets in a grid beside the rail (browse without committing). */ peek?: {
        start: (rowRect: DOMRect) => void;
        end: () => void;
    };
    /** Gallery-tile drag: highlight while a movable asset hovers this row. */ onMoveDragOver?: (over: boolean) => void;
    /** …and move it here on drop. */ onMoveDrop?: (assetIds: string[]) => void;
    moveOver?: boolean;
    readOnly?: boolean;
}) {
    const t = useTranslations$1();
    if (renaming) {
        return (<div style={{
            paddingLeft: 8 + depth * 14
        }} className="flex h-7 items-center pr-2">
        <Input autoFocus defaultValue={c.name} onKeyDown={(e)=>{
            if (e.key === 'Enter') onCommitRename((e.target as HTMLInputElement).value);
            if (e.key === 'Escape') onCancelRename();
        }} onBlur={(e)=>onCommitRename(e.target.value)} className="h-7 text-sm"/>
      </div>);
    }
    return (<>
      <div onContextMenu={onContext} onMouseEnter={(e)=>{
        peek?.start(e.currentTarget.getBoundingClientRect());
    }} onMouseLeave={()=>{
        peek?.end();
    }} onDragOver={(e)=>{
        if (!e.dataTransfer.types.includes('application/x-martini-library-move') && !e.dataTransfer.types.includes('application/x-martini-library-move-many')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onMoveDragOver?.(true);
    }} onDragLeave={()=>onMoveDragOver?.(false)} onDrop={(e)=>{
        const assetIds = libraryMoveIds(e.dataTransfer);
        if (!assetIds.length) return;
        e.preventDefault();
        onMoveDragOver?.(false);
        onMoveDrop?.(assetIds);
    }} style={{
        paddingLeft: 8 + depth * 14
    }} className={cn('group flex h-7 items-center rounded-md pr-2 transition-colors', selected ? 'bg-secondary/80' : 'hover:bg-secondary/40', moveOver && 'bg-secondary/50 ring-1 ring-inset ring-foreground/40')}>
        <span className="size-1.5 shrink-0"/>
        <button draggable={!readOnly} data-row onDragStart={(e)=>e.dataTransfer.setData('text/plain', c.id)} onClick={onOpen} onDoubleClick={readOnly ? undefined : onStartRename} title={t("5hCTbE", {
        count: String(count),
        plural: count === 1 ? '' : 's'
    })} className="flex h-7 min-w-0 flex-1 items-center gap-2 text-left">
          {thumb ? (<img src={thumb} alt="" loading="lazy" draggable={false} className="size-5 shrink-0 rounded border border-border/60 object-cover"/>) : (<div className="size-5 shrink-0 rounded border border-border/60 bg-secondary/40"/>)}
          <span className={cn('min-w-0 flex-1 truncate text-sm', selected ? 'text-foreground' : 'text-foreground/80')}>
            {c.name}
          </span>
          {c.sample && (<span title={t("I1nJc3")} className="shrink-0 rounded bg-secondary px-1 py-px text-[9px] uppercase tracking-wide text-muted-foreground/60">
              {t("4CrCbD")}
            </span>)}
        </button>
      </div>
    </>);
}
// --- Folder header (disclosure + drop target + right-click menu) -------------
function FolderHeader({ label, path, count, depth, collapsed, dropping, renaming, onToggle, onContext, onStartRename, onCommitRename, onCancelRename, onDropCollection, onDropFiles, onHover }: {
    label: string;
    path: string;
    count: number;
    depth: number;
    collapsed: boolean;
    dropping: boolean;
    renaming?: boolean;
    onToggle: () => void;
    onContext?: (e: React.MouseEvent) => void;
    /** Double-click the header starts an inline rename. */ onStartRename?: () => void;
    onCommitRename?: (name: string) => void;
    onCancelRename?: () => void;
    onDropCollection?: (collectionId: string) => void;
    onDropFiles?: (files: File[]) => void;
    onHover?: (path: string | null) => void;
}) {
    const t = useTranslations$1();
    const droppable = !!(onDropCollection || onDropFiles);
    if (renaming) {
        return (<div style={{
            paddingLeft: 8 + depth * 14
        }} className="flex h-7 items-center pr-2">
        <Input autoFocus defaultValue={label} onKeyDown={(e)=>{
            if (e.key === 'Enter') onCommitRename?.((e.target as HTMLInputElement).value);
            if (e.key === 'Escape') onCancelRename?.();
        }} onBlur={(e)=>onCommitRename?.(e.target.value)} className="h-7 text-sm"/>
      </div>);
    }
    return (<button onClick={onToggle} onDoubleClick={onStartRename} onContextMenu={onContext} onDragOver={droppable ? (e)=>{
        e.preventDefault();
        onHover?.(path);
    } : undefined} onDragLeave={droppable ? ()=>onHover?.(null) : undefined} onDrop={droppable ? (e)=>{
        e.preventDefault();
        e.stopPropagation();
        onHover?.(null);
        if (e.dataTransfer.types.includes('Files')) {
            const files = Array.from(e.dataTransfer.files);
            if (files.length) onDropFiles?.(files);
        } else {
            const cid = e.dataTransfer.getData('text/plain');
            if (cid) onDropCollection?.(cid);
        }
    } : undefined} title={t("gIcPUE", {
        count: String(count),
        plural: count === 1 ? '' : 's'
    })} style={{
        paddingLeft: 8 + depth * 14
    }} className={cn('flex h-7 w-full items-center gap-1.5 rounded-md pr-2 text-left text-xs text-muted-foreground transition-colors hover:text-foreground', dropping && 'text-foreground ring-1 ring-inset ring-foreground/30')}>
      <ChevronRight className={cn('size-3 shrink-0 transition-transform', !collapsed && 'rotate-90')}/>
      <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
      <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground/50">{count}</span>
    </button>);
}
// --- Panel -----------------------------------------------------------------
const MENU_ITEM = 'w-full rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground';
/** The library's shape as a FAN-OUT: one folder on top, its collections side
 * by side as columns, each collection's assets hanging beneath it. Icon
 * placeholders, not photos — the structure is the message. */ export function LibraryConceptDiagram({ wide }: {
    wide?: boolean;
}) {
    const t = useTranslations$1();
    // The lesson is the ATOM: one collection = one subject, holding EVERYTHING
    // about it — stills, motion, and the words that describe it (text chips).
    // Deliberately no folder tier here (folders are filing, not the concept),
    // and one subject is a PLACE so "subject" doesn't read as "person".
    const columns: {
        name: string;
        place?: boolean;
        assets: {
            kind: 'still' | 'motion' | 'text';
            label: string;
        }[];
    }[] = [
        {
            name: 'Sarah',
            assets: [
                {
                    kind: 'still',
                    label: 'Happy close-up · still'
                },
                {
                    kind: 'still',
                    label: 'Red dress · still'
                },
                {
                    kind: 'motion',
                    label: 'Walking · motion'
                },
                {
                    kind: 'text',
                    label: 'Look — "late 20s, dark hair, warm smile" · text'
                }
            ]
        },
        {
            name: 'Neon Bar',
            place: true,
            assets: [
                {
                    kind: 'still',
                    label: 'Wide from the door · still'
                },
                {
                    kind: 'still',
                    label: 'Booth corner · still'
                },
                {
                    kind: 'text',
                    label: 'Mood — "rain-slick streets, pink neon" · text'
                }
            ]
        },
        {
            name: 'Fred',
            assets: [
                {
                    kind: 'still',
                    label: 'In the suit · still'
                },
                {
                    kind: 'motion',
                    label: 'Running · motion'
                },
                {
                    kind: 'text',
                    label: 'Voice — "gravel, never hurried" · text'
                }
            ]
        }
    ];
    return (<TooltipProvider delayDuration={150}>
      <div className={cn('relative rounded-lg border border-border', wide ? 'p-4 pt-5' : 'p-2.5 pt-4')}>
        { /* The FRAME is the library; the boxes inside are its subjects. */ }
        <span className="absolute left-2 top-1 text-[9px] font-medium uppercase tracking-wider text-muted-foreground/50">
          Your library
        </span>
        { /* Collections — each one a boxed card holding everything about its subject */ }
        <div className="grid grid-cols-3 items-start gap-1.5">
          {columns.map((c)=>(<div key={c.name} className={cn('rounded-md border border-border bg-secondary/20', wide ? 'p-1.5' : 'p-1')}>
              <div className="flex min-w-0 items-center justify-center gap-1 text-[11px] text-foreground">
                {c.place ? (<MapPin className="size-3 shrink-0 text-muted-foreground/70"/>) : (<User className="size-3 shrink-0 text-muted-foreground/70"/>)}
                <span className="truncate">{c.name}</span>
              </div>
              <div className="mt-1 grid grid-cols-2 justify-items-center gap-1">
                {c.assets.map((a, i)=>(<Tooltip key={i}>
                    <TooltipTrigger asChild>
                      <span className={cn('flex items-center justify-center rounded border border-border bg-secondary/40 text-muted-foreground/60 transition-colors hover:border-foreground/40 hover:text-foreground', wide ? 'size-6' : 'size-5')}>
                        {a.kind === 'motion' ? (<Video className="size-3"/>) : a.kind === 'text' ? (<Braces className="size-3"/>) : (<ImageIcon className="size-3"/>)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {t("kbgKk4", {
                name: c.name,
                label: a.label
            })}
                    </TooltipContent>
                  </Tooltip>))}
              </div>
            </div>))}
        </div>
        <div className="mt-2 border-t border-border pt-1.5 text-center text-[10px] leading-relaxed text-muted-foreground/60">
          {t("_ZEHwa")}
        </div>
      </div>
    </TooltipProvider>);
}
export default function CollectionsLibrarySection({ selectedId, onSelect, focusAsset, libraryPage, createRequestId, shelfProjectId, onInspectorOpenChange, panelWidth }: {
    /** Controlled selection by collection id (the /library page owns it); omit
   * for the in-editor panel, which portals its own floating inspector. */ selectedId?: string | null;
    onSelect?: (id: string | null) => void;
    /** Controlled-mode companion to selectedId: also open THIS asset's detail
   * inside the focused collection (library chip click in the prompt box).
   * The nonce re-triggers for repeat clicks on the same asset. */ focusAsset?: {
        assetId: string;
        nonce: number;
    } | null;
    /** Rendered as the full-width /library page (vs the in-editor panel). Picks
   * the quiet page empty state over the in-editor concept diagram. */ libraryPage?: boolean;
    /** Increment to open the New-collection form from outside (page header CTA). */ createRequestId?: number;
    /** The in-editor shelf. Cards expand into variants; tiles copy @tags / drag
   * as references — the library never creates canvas nodes. Enablement is an
   * affordance on every card (no manage mode): the grid splits into
   * "In this project" and "From library", add/remove happens in place. */ shelfProjectId?: string;
    /** In-editor: fires when the inspector column opens/closes so the host
   * panel can widen itself — the column is part of the panel's layout box
   * (the tool rail tracks panel width and moves out of the way), never a
   * free-floating portal doing coordinate math. */ onInspectorOpenChange?: (open: boolean, width?: number) => void;
    /** The host panel's BASE width (before the +420 inspector). The cast list is
   * frozen to it while the inspector is open, so the growing panel REVEALS the
   * column instead of squeezing the list — no reflow on open/close. */ panelWidth?: number;
} = {}) {
    const t = useTranslations$1();
    const placeMode = !!shelfProjectId;
    // --- Store ----------------------------------------------------------------
    const hydrated = useLibraryStore((s)=>s.hydrated);
    const accessDenied = useLibraryStore((s)=>s.accessDenied);
    const accessDeniedReason = useLibraryStore((s)=>s.accessDeniedReason);
    const guestScope = useLibraryStore((s)=>s.guestProjectScope);
    const writeAccess = useLibraryStore(selectLibraryWriteAccess);
    const canWrite = writeAccess === 'writable';
    const allCollections = useLibraryStore((s)=>s.collections);
    const allAssets = useLibraryStore((s)=>s.assets);
    const folders = useLibraryStore((s)=>s.folders);
    const tagVocab = useLibraryStore((s)=>s.tagVocab);
    const properties = useLibraryStore((s)=>s.properties);
    const hydrate = useLibraryStore((s)=>s.hydrate);
    const resetAll = useLibraryStore((s)=>s.resetAll);
    const importFiles = useLibraryStore((s)=>s.importFiles);
    const { renameCollection, deleteCollection, moveCollection, addFolder, renameFolder, deleteFolder } = libraryActions;
    // Transient feedback ("Copied @Sarah", "Moved to Characters") keyed to a row/card.
    const [flash, setFlash] = useState<{
        key: string;
        label: string;
        undo?: boolean;
    } | null>(null);
    const showFlash = useCallback((key: string, label: string)=>{
        const entry = {
            key,
            label
        };
        setFlash(entry);
        // Clear only if this flash is still the visible one (a newer one wins).
        setTimeout(()=>setFlash((f)=>(f === entry ? null : f)), 1600);
    }, []);
    const showUndoFlash = useCallback((key: string, label: string)=>{
        const entry = {
            key,
            label,
            undo: true
        };
        setFlash(entry);
        setTimeout(()=>setFlash((f)=>(f === entry ? null : f)), 5000);
    }, []);
    const copyTag = useCallback((c: LibCollection, assetName?: string)=>{
        const tag = libraryTagFor(c.name, assetName);
        void navigator.clipboard?.writeText(tag);
        showFlash(assetName ? `${c.id}:${assetName}` : c.id, `Copied ${tag}`);
        trackLibraryTagCopied({
            workspaceId: useLibraryStore.getState().workspaceId,
            refKind: assetName ? 'asset' : 'collection'
        });
    }, [
        showFlash
    ]);
    // --- Capture: drag a canvas asset INTO the library (place mode only) --------
    // The engine sets `draggedAssetId` while a node is grip/⌘-dragged; a collection
    // row is a drop target (mouseUp), exactly like the set/reference slots.
    const draggedAssetId = useCanvasStore((s)=>s.draggedAssetId);
    const capturing = canWrite && placeMode && !!draggedAssetId;
    const [captureHover, setCaptureHover] = useState<string | null>(null);
    // Variable capture: dragging a canvas variable node over the panel offers
    // "Save to Library" — promotes it to a workspace variable (linked to this
    // project immediately). The canvas variable stays; delete it if you meant move.
    const [variableDragActive, setVariableDragActive] = useState(false);
    const [variableDropHover, setVariableDropHover] = useState(false);
    useEffect(()=>{
        if (!placeMode || !canWrite) return;
        return subscribeToVariableDragActive((active)=>{
            setVariableDragActive(active);
            if (!active) setVariableDropHover(false);
        });
    }, [
        canWrite,
        placeMode
    ]);
    // Old-subject capture: dragging a subject card over the panel offers
    // "Add to Library" — converts it into a library collection (main image +
    // reference angles as variants). The canvas subject stays.
    const [subjectDragActive, setSubjectDragActive] = useState(false);
    const [subjectDropHover, setSubjectDropHover] = useState(false);
    useEffect(()=>{
        if (!placeMode || !canWrite) return;
        return subscribeToSubjectDragActive((active)=>{
            setSubjectDragActive(active);
            if (!active) setSubjectDropHover(false);
        });
    }, [
        canWrite,
        placeMode
    ]);
    const captureSubjectFromCanvas = ()=>{
        if (!canWrite) return;
        const dragged = consumeSubjectDrag();
        setSubjectDropHover(false);
        if (!dragged || !shelfProjectId) return;
        const subject = useCanvasStore.getState().subjectCanvasNodes.get(dragged.subjectId);
        if (!subject) return;
        // Project link rides inside the import batch — one undo reverses everything.
        const result = importSubjectToLibrary(subject, {
            enableForProjectId: shelfProjectId
        });
        if (!result.ok) {
            showFlash(subject.id, result.reason === 'access-denied' ? "You don't have access to this workspace" : 'No images available to save');
            return;
        }
        showFlash(result.collectionId, `${subject.name || 'Subject'} added to Library`);
    };
    const captureVariableFromCanvas = ()=>{
        if (!canWrite) return;
        const dragged = consumeVariableDrag();
        setVariableDropHover(false);
        if (!dragged || !shelfProjectId) return;
        // Copy-up as a kind='text' asset in 'Saved' (file into a subject later).
        // The canvas variable stays — existing prompt chips keep pointing at it.
        const id = libraryActions.addTextFragment(null, {
            name: dragged.name || 'Fragment',
            text: dragged.value
        });
        if (!id) {
            showFlash(shelfProjectId, "You don't have access to this workspace");
            return;
        }
        showFlash(id, `${dragged.name || 'Fragment'} saved to Library`);
    };
    const captureFromCanvas = (collectionId: string)=>{
        if (!canWrite) return;
        const st = useCanvasStore.getState();
        const a = st.draggedAssetId ? st.assets.get(st.draggedAssetId) : null;
        if (a) {
            const result = saveCanvasAssetToLibrary(a, collectionId);
            if (!result.ok && result.reason === 'no-preview') {
                showFlash(collectionId, t("2TLzkG"));
            } else if (!result.ok && result.reason === 'access-denied') {
                showFlash(collectionId, "You don't have access to this workspace");
            } else if (!result.ok && result.reason === 'not-ready') {
                showFlash(collectionId, 'Library is still loading');
            }
        }
        st.setDraggedAssetId(null);
        setCaptureHover(null);
    };
    // Per-project enablement (shelf mode reads it; manage mode edits it).
    const enabledIds = useLibraryStore((s)=>(shelfProjectId ? s.projectLinks[shelfProjectId] : undefined));
    const enabledSet = useMemo(()=>new Set(enabledIds ?? []), [
        enabledIds
    ]);
    const setEnabled = (ids: Set<string>)=>{
        if (!canWrite || !shelfProjectId) return;
        useLibraryStore.getState().dispatch({
            type: 'link.setProject',
            projectId: shelfProjectId,
            collectionIds: [
                ...ids
            ]
        }, {
            label: t("mTWFuA")
        });
        trackLibraryProjectLinkChanged({
            workspaceId: useLibraryStore.getState().workspaceId,
            projectId: shelfProjectId,
            action: 'set',
            enabledCount: ids.size
        });
    };
    const toggleEnabled = (id: string)=>{
        const next = new Set(enabledSet);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setEnabled(next);
    };
    // Live (non-deleted) rows are what the rail renders; undo restores by id.
    // The shelf shows the whole workspace pool — the grid itself splits into
    // "In this project" / "From library" (enablement in place, no mode).
    const collections = useMemo(()=>allCollections.filter((c)=>!c.deletedAt), [
        allCollections
    ]);
    const assets = useMemo(()=>allAssets.filter((a)=>!a.deletedAt), [
        allAssets
    ]);
    const liveProps = useMemo(()=>properties.filter((p)=>!p.deletedAt), [
        properties
    ]);
    // Resolve an asset's option-id attrs into a short display summary.
    const attrSummary = useCallback((a: LibAsset)=>Object.entries(a.attrs).map(([k, v])=>formatAttrValue(liveProps.find((p)=>p.key === k), v)).filter(Boolean).slice(0, 3).join(' · '), [
        liveProps
    ]);
    useEffect(()=>{
        void hydrate();
    }, [
        hydrate
    ]);
    // Canvas content-drag (grip / ⌘-drag) → drop onto a collection row.
    // --- Selection (controlled by id, or internal) ----------------------------
    const controlled = onSelect !== undefined;
    const [selectedInternal, setSelectedInternal] = useState<string | null>(null);
    const selected = controlled ? (selectedId ?? null) : selectedInternal;
    const setSelected = (id: string | null)=>{
        if (controlled) onSelect?.(id);
        else setSelectedInternal(id);
    };
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<Filters>({});
    // One event per settled query, not per keystroke.
    useEffect(()=>{
        const q = search.trim();
        if (!q) return;
        const id = window.setTimeout(()=>trackLibrarySearched({
                workspaceId: useLibraryStore.getState().workspaceId,
                queryLength: q.length
            }), 1000);
        return ()=>window.clearTimeout(id);
    }, [
        search
    ]);
    const [group, setGroup] = useState<GroupBy>(()=>readPref(KEY_GROUP, GROUPS, 'Folders'));
    const [sort, setSort] = useState<SortKey>(()=>readPref(KEY_SORT, SORTS, 'Updated'));
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    // The "From library" pool defaults collapsed once the project has a
    // cast — it's an occasional shopping trip, not primary content. An explicit
    // toggle persists; searching/filtering forces it open (results must show).
    const [poolOpenPref, setPoolOpenPref] = useState<boolean | null>(()=>{
        if (typeof window === 'undefined') return null;
        const v = localStorage.getItem(KEY_POOL_OPEN);
        return v === null ? null : v === '1';
    });
    const setPoolOpenP = (open: boolean)=>{
        setPoolOpenPref(open);
        if (typeof window !== 'undefined') localStorage.setItem(KEY_POOL_OPEN, open ? '1' : '0');
    };
    // Cast grid (place mode): ONE collection's variants open in a FLYOUT beside
    // the panel — the grid itself never reflows (expansion that shoves every
    // card below it around reads as jumping).
    const [openCast, setOpenCast] = useState<{
        id: string;
        x: number;
        y: number;
    } | null>(null);
    // Shelf lightbox: zoom/step through one collection's stills.
    const [lightbox, setLightbox] = useState<{
        collectionId: string;
        index: number;
    } | null>(null);
    const [newFolder, setNewFolder] = useState<string | null>(null);
    const [dropHover, setDropHover] = useState<string | null>(null);
    const [fileOverList, setFileOverList] = useState(false);
    // The + menu popover, and the New Collection dialog it (and page CTAs) open.
    const [ncOpen, setNcOpen] = useState(false);
    const [ncDialog, setNcDialog] = useState(false);
    const [ncName, setNcName] = useState('');
    const [communityOpen, setCommunityOpen] = useState(false);
    // A page-level "New collection" CTA bumps createRequestId to open the dialog.
    const [handledCreateReq, setHandledCreateReq] = useState(createRequestId ?? 0);
    if (createRequestId && createRequestId !== handledCreateReq) {
        setHandledCreateReq(createRequestId);
        if (canWrite) setNcDialog(true);
    }
    const [resetConfirm, setResetConfirm] = useState(false);
    // Inline rename targets + custom right-click menu.
    const [renamingCol, setRenamingCol] = useState<string | null>(null);
    const [moveOverCol, setMoveOverCol] = useState<string | null>(null);
    const [selectedShelfAssets, setSelectedShelfAssets] = useState<Set<string>>(new Set());
    const [shelfSelectionAnchor, setShelfSelectionAnchor] = useState<string | null>(null);
    const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
    const [ctxMenu, setCtxMenu] = useState<{
        x: number;
        y: number;
        target: {
            type: 'collection';
            id: string;
        } | {
            type: 'folder';
            path: string;
        } | {
            type: 'empty';
        } | {
            type: 'shelf-asset';
            assetIds: string[];
            primaryId: string;
        };
    } | null>(null);
    const [ctxMode, setCtxMode] = useState<'root' | 'move' | 'delete'>('root');
    const listRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const setGroupP = (g: GroupBy)=>{
        setGroup(g);
        if (typeof window !== 'undefined') localStorage.setItem(KEY_GROUP, g);
    };
    const setSortP = (s: SortKey)=>{
        setSort(s);
        if (typeof window !== 'undefined') localStorage.setItem(KEY_SORT, s);
    };
    const toggleCollapse = (path: string)=>setCollapsed((prev)=>{
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    // --- Derived facets --------------------------------------------------------
    // One workspace = one library; the store already holds only this workspace's
    // rows, so the pool is simply everything live.
    const pool = collections;
    const colById = useMemo(()=>new Map(collections.map((c)=>[
                c.id,
                c
            ])), [
        collections
    ]);
    const assetCountByCol = useMemo(()=>{
        const m = new Map<string, number>();
        for (const a of assets)m.set(a.collectionId, (m.get(a.collectionId) ?? 0) + 1);
        return m;
    }, [
        assets
    ]);
    const assetsByCol = useMemo(()=>{
        const m = new Map<string, LibAsset[]>();
        for (const a of assets){
            const arr = m.get(a.collectionId);
            if (arr) arr.push(a);
            else m.set(a.collectionId, [
                a
            ]);
        }
        return m;
    }, [
        assets
    ]);
    const selectShelfAsset = (event: React.MouseEvent, assetId: string)=>{
        const additive = event.metaKey || event.ctrlKey;
        if (!additive && !event.shiftKey) {
            setSelectedShelfAssets(new Set());
            setShelfSelectionAnchor(assetId);
            return false;
        }
        // Shift-range stays inside ONE collection: a cross-card range would run in
        // store order and sweep up assets hidden in collapsed/filtered cards
        // between the two clicks — bulk actions must never touch what you can't
        // see. Shift across cards degrades to a toggle.
        const anchorCol = assets.find((a)=>a.id === shelfSelectionAnchor)?.collectionId;
        const clickedCol = assets.find((a)=>a.id === assetId)?.collectionId;
        const range = event.shiftKey && !!anchorCol && anchorCol === clickedCol;
        const next = nextAssetSelection({
            selection: selectedShelfAssets,
            anchorId: shelfSelectionAnchor,
            clickedId: assetId,
            orderedIds: range ? (assetsByCol.get(clickedCol!) ?? []).map((a)=>a.id) : [],
            additive: additive || !range,
            range
        });
        setSelectedShelfAssets(next.selection);
        setShelfSelectionAnchor(next.anchorId);
        return true;
    };
    const openShelfAssetMenu = (event: React.MouseEvent, assetId: string)=>{
        event.preventDefault();
        event.stopPropagation();
        const assetIds = selectedShelfAssets.has(assetId) ? [
            ...selectedShelfAssets
        ] : [
            assetId
        ];
        if (!selectedShelfAssets.has(assetId)) {
            setSelectedShelfAssets(new Set([
                assetId
            ]));
            setShelfSelectionAnchor(assetId);
        }
        setCtxMode('root');
        setCtxMenu({
            x: event.clientX,
            y: event.clientY,
            target: {
                type: 'shelf-asset',
                assetIds,
                primaryId: assetId
            }
        });
    };
    useEffect(()=>{
        if (!selectedShelfAssets.size || ctxMenu) return;
        const onKey = (event: KeyboardEvent)=>{
            if (event.key !== 'Escape') return;
            setSelectedShelfAssets(new Set());
            setShelfSelectionAnchor(null);
        };
        window.addEventListener('keydown', onKey, {
            capture: true
        });
        return ()=>window.removeEventListener('keydown', onKey, {
                capture: true
            });
    }, [
        ctxMenu,
        selectedShelfAssets.size
    ]);
    // While searching, collections whose ASSETS match stay findable (rows keep
    // matching by asset content; the shelf grid auto-opens just the hits).
    const assetHits = useMemo(()=>{
        const q = search.trim().toLowerCase();
        if (!q || q.includes('#')) return null;
        const m = new Map<string, Set<string>>();
        for (const a of assets){
            const attrText = Object.entries(a.attrs).map(([k, v])=>formatAttrValue(liveProps.find((p)=>p.key === k), v)).join(' ');
            if (!`${a.name} ${attrText}`.toLowerCase().includes(q)) continue;
            const set = m.get(a.collectionId);
            if (set) set.add(a.id);
            else m.set(a.collectionId, new Set([
                a.id
            ]));
        }
        return m;
    }, [
        assets,
        search,
        liveProps
    ]);
    // Selecting opens the extension column, which IS the asset view — so
    // selection no longer expands inline tiles (that double-showed the same
    // assets and let expansions pile into a thumbnail wall). The chevron stays
    // as an explicit pin.
    const openCollection = (id: string)=>{
        setSelected(id);
    };
    // Extension-column anchoring + width (declared before peek — peekStart reads them).
    const rootRef = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<{
        left: number;
        top: number;
        height: number;
    } | null>(null);
    // Tile click = LOOK: open the extension column at that asset's detail.
    const [selectedAssetInit, setSelectedAssetInit] = useState<{
        assetId: string;
        nonce: number;
    } | null>(null);
    // Outside flows (library chip click in the prompt box) focus an asset's
    // detail alongside the controlled collection selection; the nonce makes a
    // repeat click on the same asset re-open it.
    useEffect(()=>{
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reacting to an external focus request (event-driven prop), not render state.
        if (focusAsset) setSelectedAssetInit(focusAsset);
    }, [
        focusAsset
    ]);
    const [colWidth, setColWidth] = useState(()=>{
        if (typeof window === 'undefined') return 360;
        const v = Number(localStorage.getItem(KEY_COL_WIDTH));
        return v >= 320 && v <= 640 ? v : 360;
    });
    // Pool cards fold to a single header row by default; expansion is per-card.
    const [poolExpanded, setPoolExpanded] = useState<Set<string>>(new Set());
    // Double-click a card name to rename it in place (same gesture as the table).
    const [renamingColId, setRenamingColId] = useState<string | null>(null);
    // Dragging a pool card over the "Added to this project" section adds it.
    const [addDropHover, setAddDropHover] = useState(false);
    // In-editor inline inspector column width — drag the column's left edge.
    const [insWidth, setInsWidth] = useState(()=>{
        if (typeof window === 'undefined') return 420;
        const v = Number(localStorage.getItem(KEY_INS_WIDTH));
        return v >= 320 && v <= 720 ? v : 420;
    });
    // Hover-peek: a floating tile grid beside the rail (never inline — inline
    // hover-expansion reflows the list under the cursor). Row-anchored; floats
    // past the extension column when one is open.
    const [peek, setPeek] = useState<{
        id: string;
        x: number;
        y: number;
    } | null>(null);
    const peekOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const peekCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const peekDragging = useRef(false);
    const peekStart = useCallback((id: string, rowRect: DOMRect)=>{
        if (peekCloseTimer.current) clearTimeout(peekCloseTimer.current);
        if (peekOpenTimer.current) clearTimeout(peekOpenTimer.current);
        const railRight = rootRef.current?.getBoundingClientRect().right ?? rowRect.right;
        // With the extension column open, the peek floats past it — still row-anchored vertically.
        const x = !controlled && selected && anchor ? anchor.left + colWidth + 8 : railRight + 8;
        peekOpenTimer.current = setTimeout(()=>{
            setPeek({
                id,
                x,
                y: Math.min(rowRect.top, window.innerHeight - 280)
            });
        }, 180);
    }, [
        setPeek,
        controlled,
        selected,
        anchor,
        colWidth
    ]);
    const peekEnd = useCallback(()=>{
        if (peekOpenTimer.current) clearTimeout(peekOpenTimer.current);
        if (peekCloseTimer.current) clearTimeout(peekCloseTimer.current);
        peekCloseTimer.current = setTimeout(()=>{
            if (!peekDragging.current) setPeek(null);
        }, 150);
    }, [
        setPeek
    ]);
    const peekHold = useCallback(()=>{
        if (peekCloseTimer.current) clearTimeout(peekCloseTimer.current);
    }, []);
    // Selection changes reflow the rail under the pointer, so a pending open
    // timer can fire after the hover story ended and orphan the popover
    // (blocking clicks over the inspector). Selection always dismisses.
    useEffect(()=>{
        if (peekOpenTimer.current) clearTimeout(peekOpenTimer.current);
        if (peekCloseTimer.current) clearTimeout(peekCloseTimer.current);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- selection must also cancel the PENDING timers (a render adjustment can't clear timeouts).
        setPeek(null);
    }, [
        selected
    ]);
    // Outside the editor (uncontrolled, non-place), the collection detail opens
    // as an EXTENSION COLUMN anchored to this panel's right edge. In the editor
    // the inspector is NOT a portal — it renders inline and the host panel
    // widens (see onInspectorOpenChange), so no anchor math is needed there.
    useEffect(()=>{
        if (controlled || placeMode) return;
        if (!selected) {
            const raf = requestAnimationFrame(()=>setAnchor(null));
            return ()=>cancelAnimationFrame(raf);
        }
        const measure = ()=>{
            const r = rootRef.current?.getBoundingClientRect();
            if (r) setAnchor({
                left: r.right + 8,
                top: r.top,
                height: r.height
            });
        };
        const raf = requestAnimationFrame(measure);
        const ro = rootRef.current ? new ResizeObserver(measure) : null;
        if (rootRef.current && ro) ro.observe(rootRef.current);
        window.addEventListener('resize', measure);
        return ()=>{
            cancelAnimationFrame(raf);
            ro?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [
        controlled,
        placeMode,
        selected
    ]);
    // Tell the host panel to widen while the inspector column is open. Layout
    // effect (not passive) so the panel's width flip and the column mount land
    // in ONE commit before paint — no one-frame flash of an un-widened panel.
    useLayoutEffect(()=>{
        if (!placeMode) return;
        onInspectorOpenChange?.(!!selected, insWidth);
        return ()=>onInspectorOpenChange?.(false);
    }, [
        placeMode,
        selected,
        insWidth,
        onInspectorOpenChange
    ]);
    const allTags = useMemo(()=>[
            ...new Set([
                ...tagVocab,
                ...pool.flatMap((c)=>c.tags)
            ])
        ].sort(), [
        tagVocab,
        pool
    ]);
    const filmValues = useMemo(()=>[
            ...new Set(pool.map((c)=>c.film).filter((f): f is string =>!!f))
        ].sort(), [
        pool
    ]);
    const facets = useMemo<FacetSpec[]>(()=>{
        const fs: FacetSpec[] = [
            {
                key: 'Tags',
                label: 'Tags',
                values: allTags,
                get: ()=>''
            }
        ];
        if (filmValues.length) fs.push({
            key: 'Film',
            label: 'Film',
            values: filmValues,
            get: (c)=>c.film ?? ''
        });
        return fs;
    }, [
        allTags,
        filmValues
    ]);
    const facetGet = useMemo<Record<string, (c: LibCollection) => string>>(()=>Object.fromEntries(facets.map((f)=>[
                f.key,
                f.get
            ])), [
        facets
    ]);
    const filtered = useMemo(()=>{
        const q = search.trim().toLowerCase();
        let list = pool;
        if (q) list = list.filter((c)=>`${c.name} ${c.film ?? ''} ${c.tags.join(' ')}`.toLowerCase().includes(q) || assetHits?.has(c.id));
        list = applyFilters(list, filters, facetGet);
        const count = (id: string)=>assetCountByCol.get(id) ?? 0;
        const cmp: Record<SortKey, (a: LibCollection, b: LibCollection) => number> = {
            Updated: (a, b)=>b.updatedAt - a.updatedAt,
            Name: (a, b)=>a.name.localeCompare(b.name),
            Used: (a, b)=>b.usedIn - a.usedIn,
            Assets: (a, b)=>count(b.id) - count(a.id)
        };
        return [
            ...list
        ].sort(cmp[sort]);
    }, [
        pool,
        search,
        filters,
        sort,
        facetGet,
        assetCountByCol,
        assetHits
    ]);
    const counts = (facetKey: string): Record<string, number> =>{
        const others = {
            ...filters
        };
        delete others[facetKey];
        const base = applyFilters(pool, others, facetGet);
        const out: Record<string, number> = {};
        if (facetKey === 'Tags') base.forEach((c)=>c.tags.forEach((t)=>(out[t] = (out[t] ?? 0) + 1)));
        else {
            const get = facetGet[facetKey];
            if (get) base.forEach((c)=>{
                const v = get(c);
                if (v) out[v] = (out[v] ?? 0) + 1;
            });
        }
        return out;
    };
    const filed = filtered;
    // Folder tree: explicit store folders (incl. empty ones) + folders implied by
    // collections. Empty folders render as drop targets while browsing.
    const allFolderPaths = useMemo(()=>{
        const set = new Set<string>(folders);
        filed.forEach((c)=>{
            if (c.folder) set.add(c.folder);
        });
        return set;
    }, [
        folders,
        filed
    ]);
    const tree = useMemo(()=>{
        const roots = [
            ...new Set([
                ...allFolderPaths
            ].map((p)=>p.split('/')[0]).filter(Boolean))
        ].sort();
        return roots.map((root)=>{
            const subPaths = [
                ...new Set([
                    ...allFolderPaths
                ].filter((p)=>p.startsWith(`${root}/`)).map((p)=>p.split('/').slice(0, 2).join('/')))
            ].sort();
            const inRoot = filed.filter((c)=>c.folder === root || c.folder?.startsWith(`${root}/`));
            return {
                root,
                total: inRoot.length,
                direct: filed.filter((c)=>c.folder === root),
                subs: subPaths.map((sp)=>({
                        path: sp,
                        name: sp.split('/')[1],
                        items: filed.filter((c)=>c.folder === sp)
                    }))
            };
        });
    }, [
        allFolderPaths,
        filed
    ]);
    const unfiled = useMemo(()=>filed.filter((c)=>!c.folder), [
        filed
    ]);
    const browsing = !search.trim() && Object.keys(filters).length === 0;
    // --- #tag search tokens ----------------------------------------------------
    const tagToken = /(^|\s)#([^#]*)$/.exec(search);
    const tagMatches = tagToken ? allTags.filter((t)=>t.toLowerCase().includes(tagToken[2].toLowerCase()) && !(filters.Tags ?? []).includes(t)).slice(0, 5) : [];
    const applyTag = (tag: string)=>{
        setFilters((f)=>({
                ...f,
                Tags: [
                    ...(f.Tags ?? []),
                    tag
                ]
            }));
        setSearch((s)=>s.replace(/(^|\s)#([^#]*)$/, '$1').trimEnd());
    };
    // --- Keyboard: walk rows with arrows ---------------------------------------
    const moveFocus = (delta: number)=>{
        const rows = Array.from(listRef.current?.querySelectorAll('button[data-row]') ?? []);
        if (!rows.length) return;
        const idx = rows.indexOf(document.activeElement as HTMLButtonElement);
        const next = rows[Math.min(rows.length - 1, Math.max(0, idx + delta))] ?? rows[0];
        next.focus();
    };
    // --- Actions ---------------------------------------------------------------
    // Anything created FROM a project starts in that project — a fresh
    // collection landing dimmed at the bottom of "Add from library" reads as
    // "where did it go?". No-op on /library (no project context).
    const enableInProject = (collectionId: string)=>{
        if (canWrite && shelfProjectId) libraryActions.enableCollectionForProject(shelfProjectId, collectionId);
    };
    const createCollection = ()=>{
        if (!canWrite) return;
        const name = ncName.trim();
        if (!name) return;
        const result = saveCanvasAssetsToLibrary([], {
            createCollectionName: name,
            createEmptyCollection: true,
            enableForProjectId: shelfProjectId
        });
        const id = result.collectionId;
        if (!id) return;
        setNcDialog(false);
        setNcName('');
        setSelected(id);
    };
    const commitNewFolder = ()=>{
        if (!canWrite) return;
        const name = (newFolder ?? '').trim();
        if (name) addFolder(name);
        setNewFolder(null);
    };
    const commitFolderRename = (path: string, name: string)=>{
        if (!canWrite) return;
        const seg = name.trim();
        setRenamingFolder(null);
        if (!seg) return;
        const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
        const next = parent ? `${parent}/${seg}` : seg;
        if (next !== path) renameFolder(path, next);
    };
    const importToFolder = (files: File[], folder: string | null)=>{
        if (!canWrite) return;
        void importFiles(files, null).then((id)=>{
            if (!id) return;
            enableInProject(id);
            if (folder) moveCollection(id, folder);
            openCollection(id);
            emitLibraryRevealRequest([
                id
            ], undefined, {
                refetch: false
            });
        });
    };
    const openColMenu = (e: React.MouseEvent, id: string)=>{
        e.preventDefault();
        e.stopPropagation(); // don't also trigger the rail's empty-area menu
        if (!canWrite) return;
        setCtxMode('root');
        setCtxMenu({
            x: e.clientX,
            y: e.clientY,
            target: {
                type: 'collection',
                id
            }
        });
    };
    const openFolderMenu = (e: React.MouseEvent, path: string)=>{
        e.preventDefault();
        e.stopPropagation();
        if (!canWrite) return;
        setCtxMode('root');
        setCtxMenu({
            x: e.clientX,
            y: e.clientY,
            target: {
                type: 'folder',
                path
            }
        });
    };
    // Right-click on blank rail space → the same add/options a click on "+" gives.
    const openEmptyMenu = (e: React.MouseEvent)=>{
        e.preventDefault();
        if (!canWrite) return;
        setCtxMode('root');
        setCtxMenu({
            x: e.clientX,
            y: e.clientY,
            target: {
                type: 'empty'
            }
        });
    };
    // Escape closes the variants flyout (when the context menu isn't above it).
    useEffect(()=>{
        if (!openCast) return;
        const onKey = (e: KeyboardEvent)=>{
            if (e.key === 'Escape') {
                e.stopPropagation();
                setOpenCast(null);
            }
        };
        window.addEventListener('keydown', onKey, {
            capture: true
        });
        return ()=>window.removeEventListener('keydown', onKey, {
                capture: true
            });
    }, [
        openCast
    ]);
    // Escape closes the topmost layer: while the context menu is open it wins,
    // and the event stops so the inspector/undo layers below never see it.
    useEffect(()=>{
        if (!ctxMenu) return;
        const onKey = (e: KeyboardEvent)=>{
            if (e.key === 'Escape') {
                e.stopPropagation();
                setCtxMenu(null);
            }
        };
        window.addEventListener('keydown', onKey, {
            capture: true
        });
        return ()=>window.removeEventListener('keydown', onKey, {
                capture: true
            });
    }, [
        ctxMenu
    ]);
    const closeCtx = ()=>{
        setCtxMenu(null);
        setCtxMode('root');
    };
    const renderRows = (items: LibCollection[], depth: number)=>items.map((c)=>{
            // ponytail: O(rows × assets) hero lookup; fine at sandbox scale, precompute a hero map if libraries grow large.
            const hero = heroAsset(assets, c);
            const own = assetsByCol.get(c.id) ?? [];
            return (<Row key={c.id} c={c} depth={depth} selected={selected === c.id} moveOver={moveOverCol === c.id} onMoveDragOver={canWrite ? (over)=>setMoveOverCol(over ? c.id : null) : undefined} onMoveDrop={canWrite ? (assetIds)=>{
                setMoveOverCol(null);
                const moving = assetIds.filter((id)=>assets.find((asset)=>asset.id === id)?.collectionId !== c.id);
                if (!moving.length) return;
                libraryActions.moveAssets(moving, c.id);
                showFlash(c.id, `Moved ${moving.length === 1 ? 'asset' : `${moving.length} assets`} to ${c.name}`);
            } : undefined} renaming={canWrite && renamingCol === c.id} readOnly={!canWrite} thumb={hero ? getAssetUrl(hero) : ''} count={assetCountByCol.get(c.id) ?? 0} // The rail is a list of NAMES — a click opens the collection on the
            // right. Browsing imagery happens there (and on hover-peek), never as
            // inline tiles that fight the inspector for the same job.
            onOpen={()=>openCollection(c.id)} onContext={(e)=>openColMenu(e, c.id)} onStartRename={()=>{
                if (canWrite) setRenamingCol(c.id);
            }} onCommitRename={(name)=>{
                const n = name.trim();
                if (n && n !== c.name) renameCollection(c.id, n);
                setRenamingCol(null);
            }} onCancelRename={()=>setRenamingCol(null)} peek={selected !== c.id && own.length > 0 ? {
                start: (rect)=>peekStart(c.id, rect),
                end: peekEnd
            } : undefined}/>);
        });
    // While the inspector column is open the host panel is `panelWidth + 420`;
    // freezing the list to `panelWidth` (shrink-0) lets the growing panel reveal
    // the column by overflow-clip instead of squeezing the list to zero and back.
    const inspectorOpen = placeMode && !!selected;
    return (<div className="flex h-full w-full overflow-hidden">
      <div ref={rootRef} className={cn('flex h-full min-w-0 flex-col overflow-hidden', inspectorOpen && panelWidth ? 'shrink-0' : 'flex-1')} style={inspectorOpen && panelWidth ? {
        width: panelWidth
    } : undefined}>
        { /* Mode + search + controls */ }
        <div className="flex-shrink-0 space-y-2 border-b border-border px-3 py-2">
          <div className="flex items-center gap-1">
            <span className="min-w-0 px-1 text-xs font-medium text-foreground">{t("ulh3kf")}</span>
            <span className="min-w-0 flex-1"/>
            <Popover>
              <PopoverTrigger asChild>
                <button title={t("LAx8qK")} className="flex size-4 items-center justify-center rounded-full text-[10px] text-muted-foreground/50 transition-colors hover:bg-secondary hover:text-foreground">
                  ?
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="start" className="w-[26rem] max-w-[90vw] space-y-2 p-3 text-xs leading-relaxed">
                <p className="font-medium text-foreground">{t("FK8sxD")}</p>
                <p className="text-muted-foreground">
                  {t("E_GtEc")}
                </p>
                { /* The same concept diagram the empty state teaches with. */ }
                <div className="rounded-md border border-border/60 bg-background/40 p-2">
                  <LibraryConceptDiagram/>
                </div>
                <p className="text-muted-foreground">
                  {t("L12-WU")}{' '}
                  <code className="rounded bg-muted px-1 text-foreground">@Name</code>{' '}
                  {t("847Ba0")}
                </p>
                <p className="text-muted-foreground/70">{t("3DWBSB")}</p>
              </PopoverContent>
            </Popover>
            {canWrite && (<Popover open={ncOpen} onOpenChange={(o)=>{
        setNcOpen(o);
        if (!o) setResetConfirm(false);
    }}>
                <PopoverTrigger asChild>
                  <button aria-label={t("bW7B87")} title={t("bW7B87")} className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                    <Plus className="size-3.5"/>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="end" className="w-60 p-1">
                  {// Creation first, import second, destructive dev-reset last.
    // One dialect: icon + verb phrase, no trailing ellipses.
    <div className="space-y-0.5">
                      <button onClick={()=>{
        setNcOpen(false);
        setNcDialog(true);
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                        <Plus className="size-3.5 shrink-0"/>
                        {t("36dp_F")}
                      </button>
                      <button onClick={()=>{
        setNcOpen(false);
        setNewFolder('');
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                        <FolderPlus className="size-3.5 shrink-0"/>
                        {t("VCHJad")}
                      </button>
                      <button onClick={()=>{
        setNcOpen(false);
        fileInputRef.current?.click();
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                        <Upload className="size-3.5 shrink-0"/>
                        {t("t4-k6K")}
                      </button>
                      {SAMPLE_LIBRARY_AVAILABLE && (<>
                          <div className="my-1 border-t border-border"/>
                          <button onClick={()=>{
        setCommunityOpen(true);
        setNcOpen(false);
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                            <Download className="size-3.5 shrink-0"/>
                            {t("fuD1Zd")}
                          </button>
                        </>)}
                      <div className="my-1 border-t border-border"/>
                      {resetConfirm ? (<div className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-destructive">
                          <span className="flex-1">{t("uXbz4y")}</span>
                          <button onClick={()=>{
        void resetAll();
        setSelected(null);
        setResetConfirm(false);
        setNcOpen(false);
    }} className="rounded px-1.5 py-0.5 hover:bg-destructive/10">
                            {t("N2IrpM")}
                          </button>
                          <button onClick={()=>setResetConfirm(false)} className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground">
                            {t("47FYwb")}
                          </button>
                        </div>) : (<button onClick={()=>setResetConfirm(true)} className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-destructive hover:bg-destructive/10">
                          <RotateCcw className="size-3.5 shrink-0"/>
                          {t("_bl2zA")}
                        </button>)}
                    </div>}
                </PopoverContent>
              </Popover>)}
          </div>
          { /* Canvas variable dragged over the editor: offer promotion to a
            workspace variable (mouseUp drop, like the reference slots). */ }
          {placeMode && canWrite && variableDragActive && (<div onMouseEnter={()=>setVariableDropHover(true)} onMouseLeave={()=>setVariableDropHover(false)} onMouseUp={captureVariableFromCanvas} className={cn('mx-1 flex h-12 items-center justify-center rounded-lg border border-dashed text-xs transition-colors', variableDropHover ? 'border-foreground/50 bg-secondary/60 text-foreground' : 'border-border text-muted-foreground')}>
              Save to Library — usable in every project
            </div>)}
          { /* Subject card dragged over the editor: offer conversion into a
            library collection (mouseUp drop, same grammar as variables). */ }
          {placeMode && canWrite && subjectDragActive && (<div onMouseEnter={()=>setSubjectDropHover(true)} onMouseLeave={()=>setSubjectDropHover(false)} onMouseUp={captureSubjectFromCanvas} className={cn('mx-1 flex h-12 items-center justify-center rounded-lg border border-dashed text-xs transition-colors', subjectDropHover ? 'border-foreground/50 bg-secondary/60 text-foreground' : 'border-border text-muted-foreground')}>
              Add to Library — usable in every project
            </div>)}
          { /* Search/filter/sort earn their pixels only once something exists. */ }
          {pool.length > 0 && (<>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"/>
                <Input value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter' && tagMatches.length) {
            e.preventDefault();
            applyTag(tagMatches[0]);
        } else if (e.key === 'ArrowDown' && !tagMatches.length) {
            e.preventDefault();
            moveFocus(1);
        }
    }} placeholder={t("mVxltC")} className="h-8 pl-8 pr-3 text-sm"/>
                {tagMatches.length > 0 && (<div className="absolute inset-x-0 top-full z-20 mt-1 rounded-md border border-border bg-popover p-1 shadow-lg">
                    {tagMatches.map((t, idx)=>(<button key={t} onClick={()=>applyTag(t)} className={cn('flex w-full items-center gap-1.5 rounded px-2 py-1 text-xs hover:bg-secondary', idx === 0 ? 'text-foreground' : 'text-muted-foreground')}>
                        <span className="text-muted-foreground/50">#</span>
                        {t}
                        { /* eslint-disable-next-line i18next/no-literal-string -- keyboard return-key glyph, not copy */ }
                        {idx === 0 && <span className="ml-auto text-[10px] text-muted-foreground/40">↵</span>}
                      </button>))}
                  </div>)}
              </div>
              { /* One quiet control row, one grammar: icon + current value + ⌄.
                Tooltips carry the verb (Filter / Group by / Sort) — the row
                doesn't need to spell it out. */ }
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <FilterControl facets={facets} filters={filters} counts={counts} onChange={setFilters}/>
                { /* The cast grid is a small curated set — grouping/sorting chrome
                  would outweigh it. Search + Filter still narrow it. */ }
                {!placeMode && (<>
                    <IconSelect icon={<Rows3 className="size-3.5"/>} title={t("HWWSQ_")} menuLabel={t("HWWSQ_")} value={group} options={GROUPS} onChange={(v)=>setGroupP(v as GroupBy)}/>
                    <IconSelect icon={<ArrowUpDown className="size-3.5"/>} title={t("25oM9Q")} menuLabel={t("hDI-JM")} value={sort} options={SORTS} onChange={(v)=>setSortP(v as SortKey)}/>
                  </>)}
              </div>
            </>)}
        </div>

        { /* List */ }
        <div ref={listRef} onScroll={()=>setPeek(null)} onContextMenu={canWrite ? openEmptyMenu : undefined} onKeyDown={(e)=>{
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            moveFocus(e.key === 'ArrowDown' ? 1 : -1);
        }
    }} onDragOver={(e)=>{
        if (!canWrite) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setFileOverList(true);
    }} onDragLeave={(e)=>{
        if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setFileOverList(false);
    }} onDrop={(e)=>{
        if (!canWrite) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setFileOverList(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) void importFiles(files, null).then((id)=>{
            if (!id) return;
            enableInProject(id);
            openCollection(id);
            emitLibraryRevealRequest([
                id
            ], undefined, {
                refetch: false
            });
        });
    }} className="min-h-0 flex-1 overflow-auto p-1.5">
          { /* Drag in flight: a ghost slot at the top of the list — the
              new collection, previewed where it will actually appear. Only
              the TARGET lights up (this slot on hover, or the hovered card
              via captureHover) — never the whole section. Canvas drags drop
              on mouseUp; OS file drags drop anywhere in the list, and the
              slot lights to show what that drop creates. */ }
          {canWrite && (capturing || fileOverList) && (<div onMouseEnter={()=>capturing && setCaptureHover('__new__')} onMouseLeave={()=>capturing && setCaptureHover(null)} onMouseUp={()=>{
        if (!capturing) return;
        const st = useCanvasStore.getState();
        const a = st.draggedAssetId ? st.assets.get(st.draggedAssetId) : null;
        if (a) {
            // Name the collection after what was dropped — a wall of identical
            // default rows is unreadable. Numbered fallback keeps even
            // nameless drops distinguishable.
            const n = collections.filter((x)=>/^Collection( \d+)?$/.test(x.name)).length;
            const name = canvasAssetDisplayName(a)?.slice(0, 40) ?? `Collection ${n + 1}`;
            const result = saveCanvasAssetsToLibrary([
                a
            ], {
                createCollectionName: name,
                enableForProjectId: shelfProjectId
            });
            if (result.collectionId && result.results[0]?.ok) {
                showFlash(result.collectionId, `Started “${name}”`);
            }
        }
        st.setDraggedAssetId(null);
        setCaptureHover(null);
    }} className={cn('mb-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed text-xs transition-colors', // Empty library: the slot IS the surface.
    pool.length === 0 ? 'min-h-40' : 'h-9', captureHover === '__new__' || fileOverList ? 'border-foreground/50 bg-secondary/60 text-foreground' : 'border-border text-muted-foreground')}>
              { /* + marks this as a CREATE — distinct from dropping into the
                  existing collections below. */ }
              <Plus className="size-3.5 shrink-0"/>
              Drop to create a new collection
            </div>)}
          { /* The /library page renders this beside the rail — one notice per
              surface, not two. */ }
          { /* Not gated on `hydrated`: retryLoad unhydrates while it refetches,
              and the strip must survive that window to show its spinner. The
              notice self-gates on loadFailed/retrying. */ }
          {!libraryPage && <LibraryLoadFailedNotice className="mx-2 mb-1.5"/>}
          {hydrated && guestScope && !libraryPage && pool.length > 0 && (<div className="mx-2 mb-1.5 rounded-md border border-border/50 bg-card/50 px-2.5 py-2">
              <p className="text-[11px] text-muted-foreground">{t("LB2o26")}</p>
              <p className="text-[10px] text-muted-foreground/60">
                {t("veMjT4")}
              </p>
            </div>)}
          {!hydrated ? null : accessDenied && !libraryPage ? (<LibraryAccessDeniedNotice reason={accessDeniedReason} className="min-h-full"/>) : accessDenied ? null : pool.length === 0 ? (guestScope ? (<div className="flex min-h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
                <p className="text-xs text-muted-foreground">{t("eMJ2lU")}</p>
                <p className="text-[10px] text-muted-foreground/60">
                  {t("q3UC5T")}
                </p>
              </div>) : capturing ? null : libraryPage ? (// /library page: the wall beside this rail carries the full
    // explainer — the rail stays quiet instead of repeating it.
    <div className="flex min-h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
                <p className="text-xs text-muted-foreground">{t("qvfFO9")}</p>
                <button onClick={()=>setNcDialog(true)} className="rounded px-1.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                  {t("36dp_F")}
                </button>
              </div>) : (// In-editor: show the REAL thing, not a schematic — actual sample
    // stills (the same ones Load-sample delivers), one line of
    // payoff copy, and Load sample as the hero act. Three rounds of
    // diagram iteration taught us: faces teach, lectures don't.
    <div className="flex flex-col items-center gap-4 px-4 pb-6 pt-6 text-center">
                {SAMPLE_LIBRARY_AVAILABLE && (<button onClick={()=>setCommunityOpen(true)} title={t("fuD1Zd")} className="group flex items-end justify-center">
                    {SAMPLE_PREVIEW.map((pv, i)=>(<span key={pv.name} className={cn('block overflow-hidden rounded-lg border-2 border-background shadow-lg transition-transform duration-200', i === 0 && 'h-24 w-[4.5rem] translate-x-3 rotate-[-6deg] group-hover:rotate-[-8deg]', i === 1 && 'z-10 h-28 w-20 group-hover:-translate-y-1', i === 2 && 'h-24 w-[4.5rem] -translate-x-3 rotate-[6deg] group-hover:rotate-[8deg]')}>
                        <img src={pv.url} alt={pv.name} loading="lazy" className="h-full w-full object-cover"/>
                      </span>))}
                  </button>)}
                <p className="max-w-60 text-xs leading-relaxed text-muted-foreground">
                  {t("6d2M00")}{' '}
                  <code className="rounded bg-muted px-1 text-foreground">@name</code>{' '}
                  {t("VbEYlb")}
                </p>
                <div className="flex w-full max-w-52 flex-col items-stretch gap-1">
                  {SAMPLE_LIBRARY_AVAILABLE && (<button onClick={()=>setCommunityOpen(true)} className="rounded-md bg-secondary px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-secondary/80">
                      {t("fuD1Zd")}
                    </button>)}
                  <button onClick={()=>setNcDialog(true)} className="rounded-md px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                    {t("36dp_F")}
                  </button>
                </div>
              </div>)) : (<>
              { /* New-folder inline editor. In the editor panel (placeMode) folders
                  live in the "Not in this project" section below, so the input
                  renders THERE (create-here = appears-here); only the /library
                  page uses this top slot. */ }
              {!placeMode && newFolder !== null && (<div className="mb-1 px-1">
                  <Input autoFocus value={newFolder} onChange={(e)=>setNewFolder(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter') commitNewFolder();
        if (e.key === 'Escape') setNewFolder(null);
    }} onBlur={commitNewFolder} placeholder={t("lR_7o8")} className="h-8 text-sm"/>
                </div>)}

              { /* Body */ }
              {filtered.length === 0 ? (<div className="px-3 py-10 text-center">
                  <p className="text-xs text-muted-foreground">{t("E8ODyH")}</p>
                  <button onClick={()=>{
        setSearch('');
        setFilters({});
    }} className="mt-1 text-[11px] text-muted-foreground hover:text-foreground">
                    {t("F4gyn3")}
                  </button>
                </div>) : placeMode ? (/* Cast grid — subject cards, name UNDER the hero (faces + names
                 scan together). A card CLICK expands it into a full-width
                 variants block and the card folds away (imagery never repeats).
                 Variant clicks COPY the @tag; drags carry the still into a
                 prompt as a reference. Enablement lives in place (no manage
                 mode): enabled cards form "In this project" (hover ✕ removes);
                 the rest of the workspace follows as "From library" (hover +
                 adds) — the same grammar as the @ menu. */ (()=>{
        const inProject = filtered.filter((c)=>enabledSet.has(c.id));
        const poolCards = filtered.filter((c)=>!enabledSet.has(c.id));
        // ONE row grammar for BOTH sections: name header + tile
        // row(s), and a switch that moves the collection in/out of
        // this project (replaces the old hover-✕ / dimmed hero-card
        // pair). Off rows keep a single dimmed tile row — same shape,
        // recognizable at a glance. Click = look (open the column),
        // drag = use (into a prompt), switch = use here.
        const renderCard = (c: LibCollection)=>{
            const count = assetCountByCol.get(c.id) ?? 0;
            const enabled = enabledSet.has(c.id);
            // Pool cards fold away by default — the library is for
            // browsing on demand, not a wall of every tile at once.
            const expanded = enabled || poolExpanded.has(c.id);
            const captureProps = capturing ? {
                onMouseEnter: ()=>setCaptureHover(c.id),
                onMouseLeave: ()=>setCaptureHover(null),
                onMouseUp: ()=>captureFromCanvas(c.id)
            } : {};
            // Every asset shows — the 4-col grid wraps into as many
            // rows as it takes. The panel is the working cast list;
            // hiding tiles behind a "+N" chip made you open the
            // inspector just to see what a collection holds.
            const own = assetsByCol.get(c.id) ?? [];
            const openColumn = (assetId?: string)=>{
                setSelectedAssetInit(assetId ? {
                    assetId,
                    nonce: Date.now()
                } : null);
                setSelected(c.id);
            };
            const tileDrag = (e: DragEvent, a: LibAsset)=>{
                const mediaKind = a.kind === 'image' || a.kind === 'video' ? a.kind : null;
                const url = a.kind === 'video' ? a.src : getAssetUrl(a, 'large') || getAssetUrl(a);
                if (!mediaKind || url.startsWith('blob:') || url.startsWith('idb:')) {
                    e.preventDefault();
                    return;
                }
                const requestedIds = selectedShelfAssets.has(a.id) && selectedShelfAssets.size > 1 ? [
                    ...selectedShelfAssets
                ] : [
                    a.id
                ];
                const dragAssets = requestedIds.map((id)=>assets.find((asset)=>asset.id === id)).filter((asset): asset is LibAsset =>!!asset && asset.kind === mediaKind && (asset.kind === 'image' || asset.kind === 'video') && !asset.src.startsWith('idb:'));
                if (!dragAssets.length) {
                    e.preventDefault();
                    return;
                }
                const items = dragAssets.map((asset)=>{
                    const collection = colById.get(asset.collectionId);
                    return {
                        assetId: asset.id,
                        kind: asset.kind,
                        url: asset.kind === 'video' ? asset.src : getAssetUrl(asset, 'large') || getAssetUrl(asset),
                        thumbnailUrl: getAssetUrl(asset),
                        name: asset.name,
                        width: asset.width,
                        height: asset.height,
                        libraryRef: {
                            collectionId: asset.collectionId,
                            assetId: asset.id,
                            versionId: asset.currentVersionId
                        },
                        tag: libraryTagFor(collection?.name ?? c.name, asset.name)
                    };
                });
                // eslint-disable-next-line i18next/no-literal-string -- dataTransfer MIME token
                e.dataTransfer.setData('text/plain', items.map((item)=>item.tag).join(' '));
                if (mediaKind === 'video') {
                    e.dataTransfer.setData(LIBRARY_VIDEO_DRAG_MIME, '1');
                }
                // Carries library identity so native drop consumers can
                // stamp libraryRef on the reference slot (usage links).
                // eslint-disable-next-line i18next/no-literal-string -- wire payload tag
                e.dataTransfer.setData('application/json', JSON.stringify(items.length === 1 ? {
                    type: mediaKind === 'video' ? 'library-video' : 'library-image',
                    ...items[0]
                } : {
                    type: mediaKind === 'video' ? 'library-videos' : 'library-images',
                    items: items.map(({ tag: _tag, ...item })=>item)
                }));
                // Same key the browse rows and gallery use — a tile can
                // also land on another card to MOVE the asset there.
                // eslint-disable-next-line i18next/no-literal-string -- dataTransfer MIME token
                e.dataTransfer.setData('application/x-martini-library-move', a.id);
                if (dragAssets.length > 1) {
                    e.dataTransfer.setData('application/x-martini-library-move-many', JSON.stringify({
                        ids: dragAssets.map((asset)=>asset.id)
                    }));
                }
                e.dataTransfer.effectAllowed = 'copyMove';
                // ponytail: the imageDrag bus carries ONE item — bus
                // consumers (prompt text, canvas node drop) take the
                // grabbed tile only; multi-drop lands via dataTransfer.
                if (mediaKind === 'image') {
                    setImageDrag({
                        imageUrl: url,
                        source: {
                            type: 'library',
                            libraryRef: {
                                collectionId: c.id,
                                assetId: a.id,
                                versionId: a.currentVersionId
                            },
                            collectionName: c.name,
                            assetName: a.name
                        },
                        mouseX: 0,
                        mouseY: 0,
                        isOptionHeld: false,
                        grabOffsetX: 0,
                        grabOffsetY: 0
                    });
                }
            };
            return (// The CARD is the unit: hover glows the whole thing and a
            // click anywhere opens it (tiles stopPropagation to deep-
            // open their asset) — a header-only highlight reads as two
            // separate things.
            <div key={c.id} onClick={()=>openColumn()} onContextMenu={(e)=>openColMenu(e, c.id)} title={t("v0K44F", {
                name: c.name
            })} onDragOver={(e)=>{
                if (!canWrite) return;
                if (e.dataTransfer.types.includes('Files')) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                    setMoveOverCol(c.id);
                    return;
                }
                if (!e.dataTransfer.types.includes('application/x-martini-library-move') && !e.dataTransfer.types.includes('application/x-martini-library-move-many')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setMoveOverCol(c.id);
            }} onDragLeave={(e)=>{
                if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) return;
                setMoveOverCol(null);
            }} onDrop={(e)=>{
                if (!canWrite) return;
                if (e.dataTransfer.types.includes('Files')) {
                    e.preventDefault();
                    e.stopPropagation();
                    setMoveOverCol(null);
                    const files = Array.from(e.dataTransfer.files);
                    if (files.length) {
                        void importFiles(files, c.id).then((id)=>{
                            if (!id) return;
                            enableInProject(id);
                            emitLibraryRevealRequest([
                                id
                            ], undefined, {
                                refetch: false
                            });
                        });
                    }
                    return;
                }
                const assetIds = libraryMoveIds(e.dataTransfer);
                if (!assetIds.length) return;
                e.preventDefault();
                e.stopPropagation();
                setMoveOverCol(null);
                const moving = assetIds.filter((id)=>assets.find((x)=>x.id === id)?.collectionId !== c.id);
                if (!moving.length) return;
                libraryActions.moveAssets(moving, c.id);
                showFlash(c.id, `Moved ${moving.length === 1 ? 'asset' : `${moving.length} assets`} to ${c.name}`);
            }} className={cn('group/card min-w-0 cursor-pointer rounded-lg transition-colors hover:bg-secondary/40', expanded && 'pb-1.5', selected === c.id && 'bg-secondary/40', capturing && captureHover === c.id && 'ring-1 ring-foreground/60', moveOverCol === c.id && 'bg-secondary/60 ring-1 ring-inset ring-foreground/40')} {...captureProps}>
                        { /* Header doubles as the drag handle: drop the card on a
                            folder header (here or on /library) to file it. */ }
                        <div draggable={canWrite && renamingColId !== c.id} onDragStart={(e)=>{
                e.stopPropagation();
                e.dataTransfer.setData('text/plain', c.id);
                e.dataTransfer.effectAllowed = 'move';
            }} className="flex h-8 items-center gap-1.5 rounded-md px-1.5">
                          {!enabled && (<button onClick={(e)=>{
                e.stopPropagation();
                setPoolExpanded((prev)=>{
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    return next;
                });
            }} aria-label={expanded ? t("W_V6-Y") : t("0oLj_t")} title={expanded ? t("W_V6-Y") : t("0oLj_t")} className="flex size-4 shrink-0 items-center justify-center rounded text-muted-foreground/60 hover:bg-secondary hover:text-foreground">
                              <ChevronRight className={cn('size-3 transition-transform', expanded && 'rotate-90')}/>
                            </button>)}
                          {renamingColId === c.id ? (<input autoFocus data-quiet-focus defaultValue={c.name} onClick={(e)=>e.stopPropagation()} onBlur={(e)=>{
                const n = e.target.value.trim();
                if (n && n !== c.name) libraryActions.renameCollection(c.id, n);
                setRenamingColId(null);
            }} onKeyDown={(e)=>{
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') setRenamingColId(null);
            }} className="min-w-0 flex-1 rounded bg-secondary px-1 py-0.5 text-xs font-medium text-foreground outline-none"/>) : (<span title={canWrite ? t("uk9jWc", {
                name: c.name
            }) : `${c.name} — read-only`} onDoubleClick={(e)=>{
                if (!canWrite) return;
                e.stopPropagation();
                setRenamingColId(c.id);
            }} onClick={(e)=>e.stopPropagation()} className={cn('min-w-0 truncate text-xs font-medium', canWrite && 'cursor-text', enabled ? 'text-foreground' : 'text-muted-foreground')}>
                              {c.name}
                            </span>)}
                          {c.sample && (<span className="shrink-0 rounded bg-secondary px-1 py-px text-[9px] uppercase tracking-wide text-muted-foreground/60">
                              {t("4CrCbD")}
                            </span>)}
                          {!expanded && (<span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/50">{count}</span>)}
                          <span className="min-w-0 flex-1"/>
                          { /* Quiet add/remove — the section grouping already says
                              which state a card is in, so a per-row switch is
                              redundant (and loud). "Add" invites on pool cards;
                              "Remove" appears only on hover. */ }
                          {canWrite && (<button onClick={(e)=>{
                e.stopPropagation();
                toggleEnabled(c.id);
                showFlash(c.id, enabled ? `${c.name} removed from project` : `Reference with ${libraryTagFor(c.name)} in any prompt`);
            }} title={enabled ? `Remove ${c.name} from this project` : `Add ${c.name} to this project`} className={cn('shrink-0 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover/card:opacity-100')}>
                              {enabled ? 'Remove' : 'Add'}
                            </button>)}
                        </div>
                        { /* Tiles stay visible with the inspector open — cards
                            that suddenly lose their imagery read as a glitch,
                            and the list is how you drag assets between
                            collections. Collapsed pool rows keep just the header. */ }
                        {expanded && (<div className={cn('mt-1 grid grid-cols-4 gap-1 px-1.5', !enabled && 'opacity-50 transition-opacity group-hover/card:opacity-80')}>
                            {own.map((a)=>a.kind === 'text' ? (<button key={a.id} onClick={(e)=>{
                    e.stopPropagation();
                    if (selectShelfAsset(e, a.id)) return;
                    openColumn(a.id);
                }} onContextMenu={(e)=>openShelfAssetMenu(e, a.id)} title={t("6BkQLQ", {
                    name: a.name
                })} className={cn('flex aspect-square w-full flex-col items-start gap-0.5 overflow-hidden rounded-md border border-border/60 bg-secondary/30 p-1 text-start transition-colors hover:border-foreground/40', selectedShelfAssets.has(a.id) && 'ring-2 ring-foreground ring-offset-1 ring-offset-background')}>
                                  <Braces className="size-2.5 shrink-0 text-muted-foreground/60"/>
                                  <span className="w-full truncate text-[9px] text-foreground/90">{a.name}</span>
                                  <span className="line-clamp-2 w-full text-[8px] leading-tight text-muted-foreground/70">
                                    {a.textValue}
                                  </span>
                                </button>) : (<div key={a.id} className="min-w-0">
                                  <button draggable={!getAssetUrl(a).startsWith('blob:')} onDragStart={(e)=>tileDrag(e, a)} onDragEnd={()=>clearImageDrag()} onClick={(e)=>{
                    e.stopPropagation();
                    if (selectShelfAsset(e, a.id)) return;
                    openColumn(a.id);
                }} onContextMenu={(e)=>openShelfAssetMenu(e, a.id)} title={t("RMk_yR", {
                    name: a.name
                })} className={cn('relative aspect-square w-full overflow-hidden rounded-md border border-border/60 bg-secondary/40 transition-colors hover:border-foreground/40', selectedShelfAssets.has(a.id) && 'ring-2 ring-foreground ring-offset-1 ring-offset-background')}>
                                    { /* draggable={false}: a grab that starts ON the image
                                      must source from the BUTTON's dataTransfer — a native
                                      <img> drag carries a Files payload that reads as an
                                      OS file drag (upload overlays light up). */ }
                                    <img src={getAssetUrl(a)} alt={a.name} loading="lazy" draggable={false} className="h-full w-full object-cover"/>
                                  </button>
                                  <span className="mt-0.5 block w-full truncate text-[9px] leading-tight text-muted-foreground/70">
                                    {a.name}
                                  </span>
                                </div>))}
                          </div>)}
                      </div>);
        };
        // The pool keeps the library's folder structure (root level):
        // groups render under quiet subheaders, unfiled rows first.
        // Empty folders show while browsing so a just-created one
        // appears where it will live. In-project stays flat — it's
        // a cast list, not a filing view.
        const rootOf = (c: LibCollection)=>c.folder?.split('/')[0] ?? '';
        const poolRoots = [
            ...new Set([
                ...allFolderPaths
            ].map((p)=>p.split('/')[0]).filter(Boolean))
        ].sort();
        const unfiledPool = poolCards.filter((c)=>!rootOf(c));
        const poolGroups = poolRoots.map((root)=>({
                root,
                items: poolCards.filter((c)=>rootOf(c) === root)
            })).filter((g)=>g.items.length > 0 || browsing);
        // Open when: searching/filtering (results must show), creating
        // a folder (the input lives here), the user said so, or the
        // project has no cast yet (the pool is the only content).
        const poolOpen = !browsing || newFolder !== null || (poolOpenPref ?? inProject.length === 0);
        return (<>
                      { /* Drop a "From library" card anywhere on this section to
                          add it — the same move as its Add button. */ }
                      <div onDragOver={(e)=>{
            if (!canWrite) return;
            if (e.dataTransfer.types.includes('application/x-martini-library-move')) return;
            if (!e.dataTransfer.types.includes('text/plain')) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setAddDropHover(true);
        }} onDragLeave={(e)=>{
            if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) setAddDropHover(false);
        }} onDrop={(e)=>{
            if (!canWrite) return;
            setAddDropHover(false);
            const cid = e.dataTransfer.getData('text/plain');
            const dropped = collections.find((x)=>x.id === cid);
            if (!dropped || enabledSet.has(cid)) return;
            e.preventDefault();
            enableInProject(cid);
            showFlash(cid, `Reference with ${libraryTagFor(dropped.name)} in any prompt`);
        }} className={cn('rounded-md transition-colors', addDropHover && 'bg-secondary/40 ring-1 ring-inset ring-foreground/30')}>
                        {inProject.length > 0 ? (<>
                            <div className="flex items-center gap-2 px-2 pb-0.5 pt-1.5">
                              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
                                {t("XcwuJQ")}
                              </span>
                              { /* This render tree has no ambient TooltipProvider — the
                                  one in this file scopes a sample diagram only. */ }
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="size-3 cursor-help text-muted-foreground/40 transition-colors hover:text-muted-foreground"/>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs">
                                    { /* Adding is the disclosure act of the sharing model: the
                                        project carries a usage grant to its ingredients. */ }
                                    <p>
                                      {t("qmg9XB")}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div className="flex flex-col gap-2 px-2 pb-2 pt-0.5">{inProject.map(renderCard)}</div>
                          </>) : (poolCards.length > 0 && (<p className="px-3 pb-1.5 pt-1 text-[11px] leading-snug text-muted-foreground">
                              {t("eouutj")}
                            </p>))}
                      </div>
                      {(poolCards.length > 0 || poolGroups.length > 0 || newFolder !== null) && (<>
                          { /* Collapsible: the pool is secondary — one quiet row
                              when closed, with a count so it stays discoverable.
                              "Add all" only shows open (no blind bulk-adds). */ }
                          <div className="group/pool flex items-center gap-1 px-2 pb-0.5 pt-1.5">
                            <button onClick={()=>setPoolOpenP(!poolOpen)} className="flex min-w-0 items-center gap-1 rounded text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50 transition-colors hover:text-foreground">
                              <ChevronRight className={cn('size-3 shrink-0 transition-transform', poolOpen && 'rotate-90')}/>
                              {t("7B2sAW")}
                              {!poolOpen && poolCards.length > 0 && (<span className="ms-0.5 tabular-nums normal-case text-muted-foreground/40">
                                  {poolCards.length}
                                </span>)}
                            </button>
                            <span className="min-w-0 flex-1"/>
                            {canWrite && poolOpen && poolCards.length > 0 && (<button onClick={()=>setEnabled(new Set([
                ...enabledSet,
                ...poolCards.map((c)=>c.id)
            ]))} title={t("hLvpbh")} className="shrink-0 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover/pool:opacity-100">
                                {t("pn9G6Z")}
                              </button>)}
                          </div>
                          { /* New folder is created HERE, where folders live. */ }
                          {poolOpen && newFolder !== null && (<div className="px-2 pb-1 pt-0.5">
                              <Input autoFocus value={newFolder} onChange={(e)=>setNewFolder(e.target.value)} onKeyDown={(e)=>{
            if (e.key === 'Enter') commitNewFolder();
            if (e.key === 'Escape') setNewFolder(null);
        }} onBlur={commitNewFolder} placeholder={t("lR_7o8")} className="h-8 text-sm"/>
                            </div>)}
                          {poolOpen && poolCards.length === 0 && poolGroups.length === 0 && newFolder === null && (<p className="px-3 pb-1 pt-0.5 text-[11px] leading-snug text-muted-foreground/60">
                              {t("MbV73H")}
                            </p>)}
                          { /* Collapsed rows are h-8 headers — gap-px keeps the
                              list reading as one even rhythm. */ }
                          {poolOpen && (<div className="flex flex-col gap-px px-2 pb-2 pt-1">
                              <div className="flex flex-col gap-px ps-0.5">{unfiledPool.map(renderCard)}</div>
                              {poolGroups.map((g)=>(<div key={g.root}>
                                  { /* Drop target: drag a card header here to file the
                                    collection into this folder (asset drags carry
                                    the x-martini-library-move key and are ignored —
                                    assets live in collections, not folders). */ }
                                  <div onDragOver={(e)=>{
                if (!canWrite) return;
                if (e.dataTransfer.types.includes('application/x-martini-library-move')) return;
                if (!e.dataTransfer.types.includes('text/plain')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropHover(g.root);
            }} onDragLeave={()=>setDropHover(null)} onDrop={(e)=>{
                if (!canWrite) return;
                setDropHover(null);
                const cid = e.dataTransfer.getData('text/plain');
                if (!collections.some((x)=>x.id === cid)) return;
                e.preventDefault();
                moveCollection(cid, g.root);
                showFlash(cid, `Moved to ${g.root}`);
            }} onClick={()=>toggleCollapse(g.root)} title={t("xsKYPM", {
                name: g.root
            })} className={cn('group/fold flex h-7 cursor-pointer items-center gap-1.5 rounded-md pe-1.5 ps-2 text-[11px] text-muted-foreground transition-colors', dropHover === g.root ? 'bg-secondary/60 text-foreground ring-1 ring-inset ring-foreground/30' : 'hover:bg-secondary/40')}>
                                    <ChevronRight className={cn('size-3 shrink-0 text-muted-foreground/60 transition-transform', !collapsed.has(g.root) && 'rotate-90')}/>
                                    <Folder className="size-3 shrink-0 text-muted-foreground/60"/>
                                    <span className="min-w-0 truncate">{g.root}</span>
                                    <span className="text-[10px] tabular-nums text-muted-foreground/40">
                                      {g.items.length > 0 ? g.items.length : collections.some((x)=>x.folder?.split('/')[0] === g.root) ? t("KPN8Xp") : t("qYv5bp")}
                                    </span>
                                    <span className="min-w-0 flex-1"/>
                                    { /* "Add everything in this folder" — always visible when
                                      the folder has collections not yet in the project. */ }
                                    {canWrite && g.items.length > 0 && (<button onClick={(e)=>{
                e.stopPropagation();
                setEnabled(new Set([
                    ...enabledSet,
                    ...g.items.map((x)=>x.id)
                ]));
            }} title={t("zx3Ymi", {
                name: g.root
            })} className="shrink-0 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover/fold:opacity-100">
                                        {t("pn9G6Z")}
                                      </button>)}
                                  </div>
                                  {!collapsed.has(g.root) && (<div className="flex flex-col gap-px ps-2.5">{g.items.map(renderCard)}</div>)}
                                </div>))}
                            </div>)}
                        </>)}
                    </>);
    })()) : group === 'Folders' ? (<div className="flex flex-col gap-px">
                  {tree.filter((r)=>r.total > 0 || browsing).map((r)=>(<div key={r.root}>
                        <FolderHeader label={r.root} path={r.root} count={r.total} depth={0} collapsed={collapsed.has(r.root)} dropping={dropHover === r.root} renaming={canWrite && renamingFolder === r.root} onToggle={()=>toggleCollapse(r.root)} onContext={(e)=>openFolderMenu(e, r.root)} onStartRename={canWrite ? ()=>setRenamingFolder(r.root) : undefined} onCommitRename={(name)=>commitFolderRename(r.root, name)} onCancelRename={()=>setRenamingFolder(null)} onDropCollection={canWrite ? (cid)=>{
            moveCollection(cid, r.root);
            showFlash(cid, `Moved to ${r.root}`);
        } : undefined} onDropFiles={canWrite ? (files)=>importToFolder(files, r.root) : undefined} onHover={canWrite ? setDropHover : undefined}/>
                        {!collapsed.has(r.root) && (<>
                            {r.subs.filter((s)=>s.items.length > 0 || browsing).map((s)=>(<div key={s.path}>
                                  <FolderHeader label={s.name} path={s.path} count={s.items.length} depth={1} collapsed={collapsed.has(s.path)} dropping={dropHover === s.path} renaming={canWrite && renamingFolder === s.path} onToggle={()=>toggleCollapse(s.path)} onContext={(e)=>openFolderMenu(e, s.path)} onStartRename={canWrite ? ()=>setRenamingFolder(s.path) : undefined} onCommitRename={(name)=>commitFolderRename(s.path, name)} onCancelRename={()=>setRenamingFolder(null)} onDropCollection={canWrite ? (cid)=>{
                moveCollection(cid, s.path);
                showFlash(cid, `Moved to ${s.name}`);
            } : undefined} onDropFiles={canWrite ? (files)=>importToFolder(files, s.path) : undefined} onHover={canWrite ? setDropHover : undefined}/>
                                  {!collapsed.has(s.path) && renderRows(s.items, 2)}
                                </div>))}
                            {renderRows(r.direct, 1)}
                          </>)}
                      </div>))}
                  { /* Loose collections sit plainly at the root — no "Unfiled"
                    pseudo-folder to decode. Move-to → "No folder" un-files. */ }
                  {renderRows(unfiled, 0)}
                </div>) : (<div className="flex flex-col gap-px">{renderRows(filtered, 0)}</div>)}
            </>)}
        </div>

        { /* New Collection — the app's standard dialog grammar. */ }
        <Dialog open={canWrite && ncDialog} onOpenChange={(o)=>{
        setNcDialog(o);
        if (!o) setNcName('');
    }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("L4Fcr8")}</DialogTitle>
              <DialogDescription>
                {t("D0yJlR")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input autoFocus data-quiet-focus value={ncName} onChange={(e)=>setNcName(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter') createCollection();
    }} placeholder={t("Uko9u1")} maxLength={80}/>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setNcDialog(false)}>
                {t("47FYwb")}
              </Button>
              <Button onClick={createCollection} disabled={!ncName.trim()}>
                {t("XQCXwU")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <CommunityLibraryDialog open={canWrite && communityOpen} onOpenChange={setCommunityOpen}/>

        { /* Hidden file input for "From uploads…" */ }
        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e)=>{
        if (!canWrite) return;
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (files.length) void importFiles(files, null).then((id)=>{
            if (!id) return;
            enableInProject(id);
            openCollection(id);
            emitLibraryRevealRequest([
                id
            ], undefined, {
                refetch: false
            });
        });
    }}/>
      </div>

      { /* In-editor inspector: an inline column INSIDE the panel — the panel
          widens (onInspectorOpenChange) and the tool rail, which tracks panel
          width, moves itself. Nothing floats, nothing is covered. */ }
      {placeMode && selected && (<div style={{
        width: insWidth
    }} className="relative flex h-full shrink-0 flex-col overflow-hidden border-l border-border duration-200 animate-in fade-in">
          { /* Left-edge resize — drag toward the canvas to widen. */ }
          <div onMouseDown={(e)=>{
        e.preventDefault();
        const startX = e.clientX;
        const startW = insWidth;
        const move = (ev: MouseEvent)=>setInsWidth(Math.min(720, Math.max(320, startW + (startX - ev.clientX))));
        const up = ()=>{
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            setInsWidth((w)=>{
                if (typeof window !== 'undefined') localStorage.setItem(KEY_INS_WIDTH, String(w));
                return w;
            });
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }} className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-ew-resize transition-colors hover:bg-foreground/20"/>
          <CollectionInspector key={`${selected}:${selectedAssetInit?.assetId ?? ''}:${selectedAssetInit?.nonce ?? ''}`} id={selected} embedded narrow surface="panel" initialAssetId={selectedAssetInit?.assetId} initialAssetRevealNonce={selectedAssetInit?.nonce} readOnly={!canWrite} onClose={()=>{
        setSelected(null);
        setSelectedAssetInit(null);
    }}/>
        </div>)}

      { /* Extension column (outside the editor): the collection detail slides
          out anchored to this panel's right edge. */ }
      {!controlled && !placeMode && selected && anchor && typeof document !== 'undefined' && createPortal(<div style={{
        left: anchor.left,
        top: anchor.top,
        maxHeight: anchor.height,
        width: colWidth
    }} className="fixed z-40 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xl duration-150 animate-in fade-in slide-in-from-left-2">
            <CollectionInspector key={`${selected}:${selectedAssetInit?.assetId ?? ''}:${selectedAssetInit?.nonce ?? ''}`} id={selected} embedded surface="panel" initialAssetId={selectedAssetInit?.assetId} initialAssetRevealNonce={selectedAssetInit?.nonce} readOnly={!canWrite} onClose={()=>{
        setSelected(null);
        setSelectedAssetInit(null);
    }}/>
            { /* Right-edge resize — the column grows toward the canvas. */ }
            <div onMouseDown={(e)=>{
        e.preventDefault();
        const startX = e.clientX;
        const startW = colWidth;
        const move = (ev: MouseEvent)=>{
            const w = Math.min(640, Math.max(320, startW + (ev.clientX - startX)));
            setColWidth(w);
        };
        const up = ()=>{
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            setColWidth((w)=>{
                if (typeof window !== 'undefined') localStorage.setItem(KEY_COL_WIDTH, String(w));
                return w;
            });
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }} className="absolute inset-y-0 right-0 w-2 cursor-col-resize hover:bg-foreground/15"/>
          </div>, document.body)}

      {peek && typeof document !== 'undefined' && (()=>{
        const pc = colById.get(peek.id);
        const pAssets = (assetsByCol.get(peek.id) ?? []).slice(0, 12);
        const pRest = (assetCountByCol.get(peek.id) ?? 0) - pAssets.length;
        if (!pc || !pAssets.length) return null;
        return createPortal(<div style={{
            left: peek.x,
            top: peek.y
        }} onMouseEnter={peekHold} onMouseLeave={peekEnd} className="fixed z-50 w-[248px] rounded-lg border border-border bg-card p-2 shadow-xl duration-100 animate-in fade-in">
              <div className="mb-1.5 flex items-baseline gap-1.5 px-0.5">
                <span className="min-w-0 truncate text-xs font-medium text-foreground">{pc.name}</span>
                <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground/50">
                  {assetCountByCol.get(peek.id) ?? 0}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1">
                {pAssets.map((a)=>a.kind === 'text' ? (<button key={a.id} title={a.name} onClick={()=>{
                setPeek(null);
                openCollection(peek.id);
            }} className="aspect-square w-full overflow-hidden rounded-sm border border-border transition-opacity hover:opacity-80">
                      <TextTile asset={a} compact/>
                    </button>) : (<img key={a.id} src={getAssetUrl(a)} alt={a.name} title={a.name} loading="lazy" draggable={false} onClick={()=>{
                setPeek(null);
                openCollection(peek.id);
            }} className="aspect-square w-full rounded-sm border border-border object-cover transition-opacity hover:opacity-80"/>))}
                {pRest > 0 && (<button onClick={()=>{
            setPeek(null);
            openCollection(peek.id);
        }} className="flex aspect-square w-full items-center justify-center rounded-sm border border-border bg-secondary/30 text-[10px] tabular-nums text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                    +{pRest}
                  </button>)}
              </div>
            </div>, document.body);
    })()}

      { /* Transient feedback toast — bottom-center, one at a time ("Copied
          @Sarah", "Moved to Characters", "Saved to Fred"). */ }
      {flash && typeof document !== 'undefined' && createPortal(<div className="animate-in fade-in slide-in-from-bottom-2 fixed bottom-6 left-1/2 z-[80] flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-popover px-3.5 py-1.5 text-xs text-foreground shadow-xl">
            <span>{flash.label}</span>
            {flash.undo && (<button onClick={()=>{
        useLibraryStore.getState().undo();
        setFlash(null);
    }} className="rounded-full bg-secondary px-2 py-0.5 font-medium transition-colors hover:bg-secondary/80">
                Undo
              </button>)}
          </div>, document.body)}

      { /* Variants flyout — floats beside the panel so the cast grid never
          reflows. Click a still to copy its tag; drag it into a prompt. */ }
      {openCast && placeMode && typeof document !== 'undefined' && (()=>{
        const c = collections.find((x)=>x.id === openCast.id);
        const own = assetsByCol.get(openCast.id) ?? [];
        if (!c || !own.length) return null;
        const flyRefDrag = (e: DragEvent, asset: LibAsset, url: string, tag: string)=>{
            // blob: URLs are browser-local — see startRefDrag.
            if (url.startsWith('blob:') || url.startsWith('idb:')) {
                e.preventDefault();
                return;
            }
            // eslint-disable-next-line i18next/no-literal-string -- dataTransfer MIME token
            e.dataTransfer.setData('text/plain', tag);
            if (asset.kind === 'video') {
                e.dataTransfer.setData(LIBRARY_VIDEO_DRAG_MIME, '1');
            }
            // The generation drop zones' native onDrop reads this payload;
            // the libraryRef lets them stamp usage identity on the slot.
            // eslint-disable-next-line i18next/no-literal-string -- wire payload tag
            e.dataTransfer.setData('application/json', JSON.stringify({
                type: asset.kind === 'video' ? 'library-video' : 'library-image',
                assetId: asset.id,
                url,
                thumbnailUrl: getAssetUrl(asset),
                name: asset.name,
                width: asset.width,
                height: asset.height,
                libraryRef: {
                    collectionId: c.id,
                    assetId: asset.id,
                    versionId: asset.currentVersionId
                }
            }));
            e.dataTransfer.effectAllowed = 'copy';
            if (asset.kind === 'image') {
                setImageDrag({
                    imageUrl: url,
                    source: {
                        type: 'library',
                        libraryRef: {
                            collectionId: c.id,
                            assetId: asset.id,
                            versionId: asset.currentVersionId
                        },
                        collectionName: c.name,
                        assetName: asset.name
                    },
                    mouseX: 0,
                    mouseY: 0,
                    isOptionHeld: false,
                    grabOffsetX: 0,
                    grabOffsetY: 0
                });
            }
        };
        return createPortal(<>
              <div className="fixed inset-0 z-[64]" onClick={()=>setOpenCast(null)}/>
              <div style={{
            left: openCast.x,
            top: Math.max(12, openCast.y)
        }} className="fixed z-[65] w-72 rounded-lg border border-border bg-popover p-2 shadow-2xl">
                <div className="flex h-6 items-center gap-1.5 px-0.5 pb-1">
                  <span className="min-w-0 truncate text-xs font-medium text-foreground">{c.name}</span>
                  {c.sample && (<span className="shrink-0 rounded bg-secondary px-1 py-px text-[9px] uppercase tracking-wide text-muted-foreground/60">
                      {t("4CrCbD")}
                    </span>)}
                  <span className="min-w-0 flex-1"/>
                  <button onClick={()=>copyTag(c)} title={t("yfREWf", {
            tag: libraryTagFor(c.name)
        })} className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                    {libraryTagFor(c.name)}
                  </button>
                  <button onClick={()=>setOpenCast(null)} aria-label={t("rbrahO")} className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-secondary hover:text-foreground">
                    <X className="size-3"/>
                  </button>
                </div>
                <div className="grid max-h-[60vh] grid-cols-3 gap-1.5 overflow-y-auto">
                  {own.map((a)=>{
            const props = attrSummary(a);
            const derived = !!props && (props.startsWith(a.name) || a.name === 'Asset');
            const openCtx = (e: React.MouseEvent)=>{
                openShelfAssetMenu(e, a.id);
            };
            // Text fragments have no pixels — not reference-draggable; a
            // click still copies the @tag like an image variant.
            if (a.kind === 'text') {
                return (<button key={a.id} onClick={()=>copyTag(c, a.name)} onContextMenu={openCtx} title={t("iha9NI", {
                    tag: libraryTagFor(c.name, a.name)
                })} className="aspect-square w-full overflow-hidden rounded-sm border border-border transition-opacity hover:opacity-80">
                          <TextTile asset={a}/>
                        </button>);
            }
            return (<ImagePreviewHover key={a.id} src={getAssetUrl(a, 'large') || getAssetUrl(a)} label={derived ? props : a.name} sourceLabel={derived ? null : props || null} side="right">
                        <img src={getAssetUrl(a)} alt={a.name} loading="lazy" draggable={a.kind === 'image' || a.kind === 'video'} onDragStart={(e)=>(a.kind === 'image' || a.kind === 'video') && flyRefDrag(e, a, a.kind === 'video' ? a.src : getAssetUrl(a, 'large') || getAssetUrl(a), libraryTagFor(c.name, a.name))} onDragEnd={()=>clearImageDrag()} onClick={()=>copyTag(c, a.name)} onContextMenu={openCtx} title={t("AMooiw", {
                tag: libraryTagFor(c.name, a.name)
            })} className="aspect-square w-full cursor-grab rounded-sm border border-border object-cover transition-opacity hover:opacity-80 active:cursor-grabbing"/>
                      </ImagePreviewHover>);
        })}
                </div>
              </div>
            </>, document.body);
    })()}

      { /* Shelf lightbox — zoom/step through one collection's stills. */ }
      {lightbox && typeof document !== 'undefined' && (()=>{
        const own = assetsByCol.get(lightbox.collectionId) ?? [];
        const col = collections.find((x)=>x.id === lightbox.collectionId);
        if (!own.length || !col) return null;
        const idx = Math.min(lightbox.index, own.length - 1);
        return createPortal(<AssetLightbox valueLabel={(key, value)=>formatAttrValue(liveProps.find((p)=>p.key === key), value)} assets={own} index={idx} heroAssetId={col.heroAssetId} onStep={(next)=>setLightbox({
                collectionId: lightbox.collectionId,
                index: next
            })} onClose={()=>setLightbox(null)} onSetHero={canWrite ? (assetId)=>libraryActions.setHero(lightbox.collectionId, assetId) : undefined}/>, document.body);
    })()}

      { /* Right-click menu (collection rows + folder headers) */ }
      {ctxMenu && typeof document !== 'undefined' && createPortal(<>
            <div className="fixed inset-0 z-[70]" onClick={closeCtx} onContextMenu={(e)=>{
        e.preventDefault();
        closeCtx();
    }}/>
            <div style={{
        left: Math.min(ctxMenu.x, window.innerWidth - 200),
        top: Math.min(ctxMenu.y, window.innerHeight - 260)
    }} className="fixed z-[71] w-48 rounded-md border border-border bg-popover p-1 shadow-xl">
              {ctxMenu.target.type === 'shelf-asset' ? ((()=>{
        const tgt = ctxMenu.target;
        const selectedAssets = tgt.assetIds.map((id)=>assets.find((asset)=>asset.id === id)).filter((asset): asset is LibAsset =>Boolean(asset));
        const a = assets.find((asset)=>asset.id === tgt.primaryId);
        const col = a ? colById.get(a.collectionId) : undefined;
        if (!col || !a) return null;
        if (ctxMode === 'move') {
            return (<div>
                        <div className="px-2 pb-1 pt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                          Move {selectedAssets.length === 1 ? 'asset' : `${selectedAssets.length} assets`} to
                        </div>
                        <div className="max-h-56 overflow-auto">
                          {collections.map((destination)=>(<button key={destination.id} disabled={selectedAssets.every((asset)=>asset.collectionId === destination.id)} onClick={()=>{
                    const moving = selectedAssets.filter((asset)=>asset.collectionId !== destination.id).map((asset)=>asset.id);
                    if (moving.length) libraryActions.moveAssets(moving, destination.id);
                    setSelectedShelfAssets(new Set());
                    showFlash(destination.id, `Moved ${moving.length} to ${destination.name}`);
                    closeCtx();
                }} className="flex w-full items-center rounded px-2 py-1 text-left text-xs text-foreground hover:bg-secondary disabled:opacity-40">
                              <span className="truncate">{destination.name}</span>
                            </button>))}
                        </div>
                      </div>);
        }
        return (<div className="space-y-0.5">
                      {selectedAssets.length > 1 && (<div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                          {selectedAssets.length} assets selected
                        </div>)}
                      {selectedAssets.length === 1 && (<>
                          <button onClick={()=>{
            copyTag(col, a.name);
            closeCtx();
        }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                            {t("P9xDaZ")}
                          </button>
                          <button onClick={()=>{
            const own = assetsByCol.get(a.collectionId) ?? [];
            setLightbox({
                collectionId: a.collectionId,
                index: own.findIndex((asset)=>asset.id === a.id)
            });
            closeCtx();
        }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                            {t("ocVwRw")}
                          </button>
                          {canWrite && (<button onClick={()=>{
            libraryActions.setHero(a.collectionId, a.id);
            closeCtx();
        }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                              {t("BNupBu")}
                            </button>)}
                        </>)}
                      {canWrite && (<>
                          <button onClick={()=>setCtxMode('move')} className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                            Move to
                            <ChevronRight className="size-3"/>
                          </button>
                          <button onClick={()=>{
            const ids = selectedAssets.map((asset)=>asset.id);
            libraryActions.deleteAssets(ids);
            setSelectedShelfAssets(new Set());
            closeCtx();
            showUndoFlash(a.id, `Deleted ${ids.length === 1 ? a.name : `${ids.length} assets`}`);
        }} className="w-full rounded px-2 py-1 text-left text-xs text-destructive hover:bg-destructive/10">
                            Delete
                          </button>
                        </>)}
                    </div>);
    })()) : ctxMenu.target.type === 'collection' ? ((()=>{
        const id = ctxMenu.target.id;
        const collection = colById.get(id);
        const assetCount = assetCountByCol.get(id) ?? 0;
        if (ctxMode === 'move') return (<div>
                        <div className="px-2 pb-1 pt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                          {t("AfxJ0y")}
                        </div>
                        <div className="max-h-56 overflow-auto">
                          {[
            ...allFolderPaths
        ].sort().map((p)=>(<button key={p} onClick={()=>{
                moveCollection(id, p);
                showFlash(id, `Moved to ${p}`);
                closeCtx();
            }} className="flex w-full items-center rounded px-2 py-1 text-left text-xs text-foreground hover:bg-secondary">
                              <span className="truncate">{p}</span>
                            </button>))}
                          <button onClick={()=>{
            moveCollection(id, null);
            showFlash(id, 'Moved out of folders');
            closeCtx();
        }} className="flex w-full items-center rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-secondary hover:text-foreground">
                            {t("OknGVA")}
                          </button>
                        </div>
                      </div>);
        if (ctxMode === 'delete' && collection) return (<div className="space-y-2 p-2">
                        <p className="text-xs leading-snug text-foreground">
                          Delete “{collection.name}” and its {assetCount} {assetCount === 1 ? 'asset' : 'assets'}?
                        </p>
                        <div className="flex justify-end gap-1">
                          <button onClick={()=>setCtxMode('root')} className={MENU_ITEM}>
                            Cancel
                          </button>
                          <button onClick={()=>{
            deleteCollection(id);
            if (selected === id) setSelected(null);
            closeCtx();
            showUndoFlash(id, `Deleted ${collection.name}`);
        }} className="rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10">
                            Delete
                          </button>
                        </div>
                      </div>);
        return (<div className="space-y-0.5">
                      <button onClick={()=>{
            setRenamingCol(id);
            closeCtx();
        }} className={MENU_ITEM}>
                        {t("iXNbPf")}
                      </button>
                      <button onClick={()=>setCtxMode('move')} className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                        {t("AfxJ0y")}
                        <ChevronRight className="size-3"/>
                      </button>
                      { /* Immediate — delete is soft + undoable (CMD+Z restores). */ }
                      <button onClick={()=>{
            if (assetCount > 0) {
                setCtxMode('delete');
                return;
            }
            deleteCollection(id);
            if (selected === id) setSelected(null);
            closeCtx();
            showUndoFlash(id, `Deleted ${collection?.name ?? 'collection'}`);
        }} className="w-full rounded px-2 py-1 text-left text-xs text-destructive hover:bg-destructive/10">
                        {t("K3r6DQ")}
                      </button>
                    </div>);
    })()) : ctxMenu.target.type === 'folder' ? ((()=>{
        const path = ctxMenu.target.path;
        if (ctxMode === 'delete') return (<div className="flex items-center gap-1 px-2 py-1 text-xs text-destructive">
                        <span className="flex-1">{t("OZMWOP")}</span>
                        <button onClick={()=>{
            deleteFolder(path);
            closeCtx();
        }} className="rounded px-1.5 py-0.5 hover:bg-destructive/10">
                          {t("N2IrpM")}
                        </button>
                      </div>);
        return (<div className="space-y-0.5">
                      <button onClick={()=>{
            setRenamingFolder(path);
            closeCtx();
        }} className={MENU_ITEM}>
                        {t("8ExVYq")}
                      </button>
                      <button onClick={()=>setCtxMode('delete')} className="w-full rounded px-2 py-1 text-left text-xs text-destructive hover:bg-destructive/10">
                        {t("M0iO6Z")}
                      </button>
                    </div>);
    })()) : (// Blank rail space — the creation options from the "+" menu.
    <div className="space-y-0.5">
                  <button onClick={()=>{
        closeCtx();
        setNcDialog(true);
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                    <Plus className="size-3.5 shrink-0"/>
                    {t("36dp_F")}
                  </button>
                  <button onClick={()=>{
        closeCtx();
        setNewFolder('');
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                    <FolderPlus className="size-3.5 shrink-0"/>
                    {t("VCHJad")}
                  </button>
                  <button onClick={()=>{
        closeCtx();
        fileInputRef.current?.click();
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                    <Upload className="size-3.5 shrink-0"/>
                    {t("t4-k6K")}
                  </button>
                  {SAMPLE_LIBRARY_AVAILABLE && (<>
                      <div className="my-1 border-t border-border"/>
                      <button onClick={()=>{
        setCommunityOpen(true);
        closeCtx();
    }} className={cn(MENU_ITEM, 'flex items-center gap-2')}>
                        <Download className="size-3.5 shrink-0"/>
                        {t("fuD1Zd")}
                      </button>
                    </>)}
                </div>)}
            </div>
          </>, document.body)}
    </div>);
}
